// apps/admin-ui/src/generated/contractSources.ts
// Auto-generated — do not edit by hand
// Regenerate with: ./scripts/sync-contract-sources.sh
// ALL RIGHTS RESERVED

/**
 * Contract source code embedded at build time.
 * The SourceViewerDialog reads from this map after OTP re-authentication.
 */
export const CONTRACT_SOURCES: Record<string, string> = {
  'contracts/compliance/AmlContract.ts': `// contracts/compliance/AmlContract.ts
// ALL RIGHTS RESERVED

/**
 * AML Contract
 *
 * Business rules for Anti-Money Laundering compliance.
 *
 * Contract Reference: Section 1.1.2
 * "Token Ownership, in compliance with Regulation A Tier 2, KYC/AML reporting
 * requirements for initial issuance reports to the US Securities and Exchange
 * Commission, FINRA and FinCEN"
 *
 * Contract Reference: Section 3.1
 * "CCC shall ensure compliance with all applicable regulations, including
 * AML/KYC standards"
 *
 * @module contracts/compliance/AmlContract
 */

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════

/** Suspicious Activity Report (SAR) threshold (USD) */
export const SAR_THRESHOLD = 10000;

/** Currency Transaction Report (CTR) threshold (USD) */
export const CTR_THRESHOLD = 10000;

/** Structuring detection window (days) */
export const STRUCTURING_WINDOW_DAYS = 7;

/** Structuring detection threshold (multiple transactions below SAR) */
export const STRUCTURING_THRESHOLD = 0.8; // 80% of SAR threshold

/** High-risk country list update frequency (days) */
export const COUNTRY_LIST_UPDATE_DAYS = 30;

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export type RiskLevel = 'low' | 'medium' | 'high' | 'prohibited';
export type AlertType = 'sar' | 'ctr' | 'structuring' | 'high_risk_country' | 'sanctions';

export interface Transaction {
  id: string;
  userId: string;
  amount: number;
  type: string;
  timestamp: Date;
  counterpartyId?: string;
}

export interface AmlCheckInput {
  userId: string;
  transaction: Transaction;
  recentTransactions: Transaction[];
  userCountry: string;
  counterpartyCountry?: string;
}

export interface AmlCheckOutput {
  allowed: boolean;
  riskLevel: RiskLevel;
  alerts: AmlAlert[];
  requiresReview: boolean;
  requiresReport: boolean;
}

export interface AmlAlert {
  type: AlertType;
  severity: RiskLevel;
  message: string;
  transactionId: string;
  timestamp: Date;
}

export interface StructuringAnalysis {
  isStructuring: boolean;
  transactionCount: number;
  totalAmount: number;
  averageAmount: number;
  pattern: string;
}

// ═══════════════════════════════════════════════════════
// HIGH RISK COUNTRIES (FATF Grey List - example)
// ═══════════════════════════════════════════════════════

const HIGH_RISK_COUNTRIES = [
  'KP', // North Korea
  'IR', // Iran
  'MM', // Myanmar
  'SY', // Syria
];

const GREY_LIST_COUNTRIES = [
  'AL', 'BF', 'CM', 'HR', 'CD', 'GI', 'HT', 'JM',
  'JO', 'ML', 'MZ', 'NG', 'PA', 'PH', 'SN', 'ZA',
  'SS', 'TZ', 'TR', 'UG', 'AE', 'VN', 'YE',
];

// ═══════════════════════════════════════════════════════
// CONTRACT
// ═══════════════════════════════════════════════════════

export const AmlContract = {
  // Constants exposed for transparency
  SAR_THRESHOLD,
  CTR_THRESHOLD,
  STRUCTURING_WINDOW_DAYS,
  STRUCTURING_THRESHOLD,
  COUNTRY_LIST_UPDATE_DAYS,

  /**
   * Perform AML check on transaction
   */
  check(input: AmlCheckInput): AmlCheckOutput {
    const alerts: AmlAlert[] = [];
    let requiresReview = false;
    let requiresReport = false;
    let riskLevel: RiskLevel = 'low';

    // Check country risk
    const countryRisk = this.checkCountryRisk(
      input.userCountry,
      input.counterpartyCountry
    );
    if (countryRisk.riskLevel !== 'low') {
      alerts.push({
        type: 'high_risk_country',
        severity: countryRisk.riskLevel,
        message: countryRisk.message,
        transactionId: input.transaction.id,
        timestamp: new Date(),
      });
      if (countryRisk.riskLevel === 'prohibited') {
        return {
          allowed: false,
          riskLevel: 'prohibited',
          alerts,
          requiresReview: true,
          requiresReport: true,
        };
      }
      riskLevel = countryRisk.riskLevel;
      requiresReview = true;
    }

    // Check for CTR threshold
    if (input.transaction.amount >= CTR_THRESHOLD) {
      alerts.push({
        type: 'ctr',
        severity: 'medium',
        message: \`Transaction of $\${input.transaction.amount} exceeds CTR threshold of $\${CTR_THRESHOLD}\`,
        transactionId: input.transaction.id,
        timestamp: new Date(),
      });
      requiresReport = true;
      if (riskLevel === 'low') riskLevel = 'medium';
    }

    // Check for structuring
    const structuringAnalysis = this.detectStructuring(
      input.recentTransactions,
      input.transaction
    );
    if (structuringAnalysis.isStructuring) {
      alerts.push({
        type: 'structuring',
        severity: 'high',
        message: \`Potential structuring detected: \${structuringAnalysis.transactionCount} transactions totaling $\${structuringAnalysis.totalAmount} in \${STRUCTURING_WINDOW_DAYS} days\`,
        transactionId: input.transaction.id,
        timestamp: new Date(),
      });
      requiresReview = true;
      requiresReport = true;
      riskLevel = 'high';
    }

    return {
      allowed: riskLevel !== 'prohibited' as RiskLevel,
      riskLevel,
      alerts,
      requiresReview,
      requiresReport,
    };
  },

  /**
   * Check country risk level
   */
  checkCountryRisk(
    userCountry: string,
    counterpartyCountry?: string
  ): { riskLevel: RiskLevel; message: string } {
    // Check prohibited countries
    if (HIGH_RISK_COUNTRIES.includes(userCountry)) {
      return {
        riskLevel: 'prohibited',
        message: \`User country \${userCountry} is on prohibited list\`,
      };
    }

    if (counterpartyCountry && HIGH_RISK_COUNTRIES.includes(counterpartyCountry)) {
      return {
        riskLevel: 'prohibited',
        message: \`Counterparty country \${counterpartyCountry} is on prohibited list\`,
      };
    }

    // Check grey list countries
    if (GREY_LIST_COUNTRIES.includes(userCountry)) {
      return {
        riskLevel: 'high',
        message: \`User country \${userCountry} is on FATF grey list\`,
      };
    }

    if (counterpartyCountry && GREY_LIST_COUNTRIES.includes(counterpartyCountry)) {
      return {
        riskLevel: 'medium',
        message: \`Counterparty country \${counterpartyCountry} is on FATF grey list\`,
      };
    }

    return { riskLevel: 'low', message: '' };
  },

  /**
   * Detect potential structuring behavior
   */
  detectStructuring(
    recentTransactions: Transaction[],
    newTransaction: Transaction
  ): StructuringAnalysis {
    const windowStart = Date.now() - STRUCTURING_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    const windowTransactions = recentTransactions.filter(
      (t) => t.timestamp.getTime() >= windowStart
    );

    // Include new transaction
    const allTransactions = [...windowTransactions, newTransaction];

    // Filter for transactions below SAR threshold but above structuring threshold
    const suspiciousTransactions = allTransactions.filter(
      (t) =>
        t.amount >= SAR_THRESHOLD * STRUCTURING_THRESHOLD &&
        t.amount < SAR_THRESHOLD
    );

    const totalAmount = suspiciousTransactions.reduce((sum, t) => sum + t.amount, 0);
    const averageAmount =
      suspiciousTransactions.length > 0
        ? totalAmount / suspiciousTransactions.length
        : 0;

    // Structuring detected if:
    // 1. Multiple transactions just below threshold
    // 2. Total exceeds SAR threshold
    const isStructuring =
      suspiciousTransactions.length >= 3 && totalAmount >= SAR_THRESHOLD;

    return {
      isStructuring,
      transactionCount: suspiciousTransactions.length,
      totalAmount: Math.round(totalAmount * 100) / 100,
      averageAmount: Math.round(averageAmount * 100) / 100,
      pattern: isStructuring
        ? \`\${suspiciousTransactions.length} transactions averaging $\${averageAmount.toFixed(2)}\`
        : 'No pattern detected',
    };
  },

  /**
   * Check if transaction requires SAR filing
   */
  requiresSar(amount: number, riskLevel: RiskLevel): boolean {
    // SAR required for:
    // 1. Any transaction >= threshold
    // 2. High-risk transactions regardless of amount
    return amount >= SAR_THRESHOLD || riskLevel === 'high';
  },

  /**
   * Check if transaction requires CTR filing
   */
  requiresCtr(amount: number): boolean {
    return amount >= CTR_THRESHOLD;
  },

  /**
   * Get country risk level
   */
  getCountryRiskLevel(countryCode: string): RiskLevel {
    if (HIGH_RISK_COUNTRIES.includes(countryCode)) return 'prohibited';
    if (GREY_LIST_COUNTRIES.includes(countryCode)) return 'high';
    return 'low';
  },

  /**
   * Check if country is allowed
   */
  isCountryAllowed(countryCode: string): boolean {
    return !HIGH_RISK_COUNTRIES.includes(countryCode);
  },
};
`,

  'contracts/compliance/KycContract.ts': `// contracts/compliance/KycContract.ts
// ALL RIGHTS RESERVED

/**
 * KYC Contract
 *
 * Business rules for Know Your Customer requirements.
 *
 * Contract Reference: Section 1.1.2
 * "Token Ownership, in compliance with Regulation A Tier 2, KYC/AML reporting
 * requirements for initial issuance reports to the US Securities and Exchange
 * Commission, FINRA and FinCEN"
 *
 * Contract Reference: Section 3.1
 * "CCC shall ensure compliance with all applicable regulations, including
 * AML/KYC standards, SEC requirements, and global financial standards"
 *
 * @module contracts/compliance/KycContract
 */

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════

/** KYC levels */
export const KYC_LEVELS = {
  NONE: 'none',
  PENDING: 'pending',
  BASIC: 'basic',
  VERIFIED: 'verified',
  ENHANCED: 'enhanced',
  REJECTED: 'rejected',
} as const;

/** Limits by KYC level (in USD) */
export const KYC_LIMITS = {
  none: {
    dailyMint: 0,
    dailyWithdraw: 0,
    dailyTransfer: 0,
    dailyExchange: 0,
  },
  pending: {
    dailyMint: 0,
    dailyWithdraw: 0,
    dailyTransfer: 0,
    dailyExchange: 0,
  },
  basic: {
    dailyMint: 1000,
    dailyWithdraw: 500,
    dailyTransfer: 1000,
    dailyExchange: 1000,
  },
  verified: {
    dailyMint: 50000,
    dailyWithdraw: 25000,
    dailyTransfer: 50000,
    dailyExchange: 50000,
  },
  enhanced: {
    dailyMint: 500000,
    dailyWithdraw: 250000,
    dailyTransfer: 500000,
    dailyExchange: 250000,
  },
  rejected: {
    dailyMint: 0,
    dailyWithdraw: 0,
    dailyTransfer: 0,
    dailyExchange: 0,
  },
} as const;

/** Required documents by KYC level */
export const KYC_REQUIREMENTS = {
  basic: ['government_id'],
  verified: ['government_id', 'proof_of_address', 'selfie'],
  enhanced: ['government_id', 'proof_of_address', 'selfie', 'source_of_funds'],
} as const;

/** KYC expiry in days */
export const KYC_EXPIRY_DAYS = 365;

/** Re-verification threshold (USD total volume) */
export const REVERIFICATION_THRESHOLD = 100000;

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export type KycLevel = typeof KYC_LEVELS[keyof typeof KYC_LEVELS];
export type OperationType = 'mint' | 'withdraw' | 'transfer' | 'exchange';

export interface KycStatus {
  level: KycLevel;
  verifiedAt?: Date;
  expiresAt?: Date;
  documents: string[];
  totalVolume: number;
}

export interface KycCheckInput {
  userId: string;
  kycStatus: KycStatus;
  operation: OperationType;
  amount: number;
}

export interface KycCheckOutput {
  allowed: boolean;
  reason?: string;
  currentLevel: KycLevel;
  requiredLevel: KycLevel;
  limit: number;
  needsReverification: boolean;
}

export interface KycValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

// ═══════════════════════════════════════════════════════
// CONTRACT
// ═══════════════════════════════════════════════════════

export const KycContract = {
  // Constants exposed for transparency
  KYC_LEVELS,
  KYC_LIMITS,
  KYC_REQUIREMENTS,
  KYC_EXPIRY_DAYS,
  REVERIFICATION_THRESHOLD,

  /**
   * Check if operation is allowed for user's KYC level
   */
  checkOperation(input: KycCheckInput): KycCheckOutput {
    const limits = KYC_LIMITS[input.kycStatus.level];
    const limitKey = \`daily\${input.operation.charAt(0).toUpperCase()}\${input.operation.slice(1)}\` as keyof typeof limits;
    const limit = limits[limitKey];

    // Check if KYC is expired
    const isExpired = input.kycStatus.expiresAt
      ? new Date() > input.kycStatus.expiresAt
      : false;

    // Check if re-verification needed
    const needsReverification =
      input.kycStatus.totalVolume >= REVERIFICATION_THRESHOLD &&
      input.kycStatus.level !== 'enhanced';

    // Determine required level
    let requiredLevel: KycLevel = 'verified';
    if (input.amount > KYC_LIMITS.verified[limitKey]) {
      requiredLevel = 'enhanced';
    } else if (input.amount <= KYC_LIMITS.basic[limitKey]) {
      requiredLevel = 'basic';
    }

    // Check if allowed
    const levelOrder = ['none', 'pending', 'rejected', 'basic', 'verified', 'enhanced'];
    const currentLevelIndex = levelOrder.indexOf(input.kycStatus.level);
    const requiredLevelIndex = levelOrder.indexOf(requiredLevel);

    const hasRequiredLevel = currentLevelIndex >= requiredLevelIndex;
    const withinLimit = input.amount <= limit;

    let allowed = hasRequiredLevel && withinLimit && !isExpired;
    let reason: string | undefined;

    if (isExpired) {
      allowed = false;
      reason = 'KYC verification has expired. Please re-verify.';
    } else if (!hasRequiredLevel) {
      allowed = false;
      reason = \`KYC level \${requiredLevel} required for this operation\`;
    } else if (!withinLimit) {
      allowed = false;
      reason = \`Amount $\${input.amount} exceeds daily limit of $\${limit} for \${input.kycStatus.level} KYC\`;
    }

    return {
      allowed,
      reason,
      currentLevel: input.kycStatus.level,
      requiredLevel,
      limit,
      needsReverification,
    };
  },

  /**
   * Validate KYC status
   */
  validate(kycStatus: KycStatus): KycValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check level
    if (!Object.values(KYC_LEVELS).includes(kycStatus.level)) {
      errors.push(\`Invalid KYC level: \${kycStatus.level}\`);
    }

    // Check expiry
    if (kycStatus.expiresAt && new Date() > kycStatus.expiresAt) {
      warnings.push('KYC verification has expired');
    }

    // Check re-verification threshold
    if (
      kycStatus.totalVolume >= REVERIFICATION_THRESHOLD &&
      kycStatus.level !== 'enhanced'
    ) {
      warnings.push(
        \`Total volume ($\${kycStatus.totalVolume}) exceeds re-verification threshold. Enhanced KYC recommended.\`
      );
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  },

  /**
   * Get limits for KYC level
   */
  getLimits(level: KycLevel): typeof KYC_LIMITS[KycLevel] {
    return KYC_LIMITS[level];
  },

  /**
   * Get required documents for level upgrade
   */
  getRequiredDocuments(targetLevel: 'basic' | 'verified' | 'enhanced'): readonly string[] {
    return KYC_REQUIREMENTS[targetLevel];
  },

  /**
   * Calculate KYC expiry date
   */
  calculateExpiryDate(verifiedAt: Date): Date {
    const expiry = new Date(verifiedAt);
    expiry.setDate(expiry.getDate() + KYC_EXPIRY_DAYS);
    return expiry;
  },

  /**
   * Check if level is sufficient for operation
   */
  isLevelSufficient(
    currentLevel: KycLevel,
    requiredLevel: KycLevel
  ): boolean {
    const levelOrder = ['none', 'pending', 'rejected', 'basic', 'verified', 'enhanced'];
    return levelOrder.indexOf(currentLevel) >= levelOrder.indexOf(requiredLevel);
  },
};
`,

  'contracts/exchange/ExchangeContract.ts': `// contracts/exchange/ExchangeContract.ts
// ALL RIGHTS RESERVED

/**
 * Exchange Contract
 *
 * Business rules for CXXR buy/sell operations via exchange service.
 *
 * Contract Reference: Section 1.1.1
 * "Integration of centralized exchange (CEX) connectivity"
 *
 * Contract Reference: Section 1.2
 * "CCC shall prioritize the integration of Centralized Exchange (CEX)"
 *
 * @module contracts/exchange/ExchangeContract
 */

import { FeeContract } from '../fees/FeeContract.js';

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════

/** Minimum exchange amount in USD */
export const MIN_EXCHANGE_AMOUNT = 10;

/** Maximum exchange amount per transaction in USD */
export const MAX_EXCHANGE_AMOUNT = 10000;

/** Maximum daily exchange amount per user in USD */
export const MAX_DAILY_EXCHANGE_AMOUNT = 50000;

/** Maximum monthly exchange amount per user in USD */
export const MAX_MONTHLY_EXCHANGE_AMOUNT = 250000;

/** Exchange fee percentage (same as FeeContract.EXCHANGE_FEE) */
const EXCHANGE_FEE = 0.5;

/** Required KYC level for exchange */
export const EXCHANGE_REQUIRED_KYC_LEVEL = 'verified';

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export type ExchangeSide = 'buy' | 'sell';
export type ExchangeKycStatus = 'none' | 'pending' | 'verified' | 'rejected';

export interface ExchangeInput {
  userId: string;
  side: ExchangeSide;
  amount: number; // USD for buy, CXXR for sell
  kycStatus: ExchangeKycStatus;
  cxxrBalance?: number; // Required for sell
  dailyExchangeAmount?: number;
  monthlyExchangeAmount?: number;
}

export interface ExchangeOutput {
  side: ExchangeSide;
  inputAmount: number;
  inputCurrency: 'USD' | 'CXXR';
  outputAmount: number;
  outputCurrency: 'USD' | 'CXXR';
  feeAmount: number;
  feePercent: number;
  feeDistribution: {
    cxxr: number;
    ccc: number;
    reserve: number;
  };
}

export interface ExchangeValidationResult {
  valid: boolean;
  errors: string[];
}

export interface ExchangeLimits {
  min: number;
  max: number;
  dailyRemaining: number;
  monthlyRemaining: number;
}

// ═══════════════════════════════════════════════════════
// CONTRACT
// ═══════════════════════════════════════════════════════

export const ExchangeContract = {
  // Constants exposed for transparency
  MIN_EXCHANGE_AMOUNT,
  MAX_EXCHANGE_AMOUNT,
  MAX_DAILY_EXCHANGE_AMOUNT,
  MAX_MONTHLY_EXCHANGE_AMOUNT,
  EXCHANGE_FEE,
  EXCHANGE_REQUIRED_KYC_LEVEL,

  /**
   * Validate exchange operation
   */
  validate(input: ExchangeInput): ExchangeValidationResult {
    const errors: string[] = [];

    // User validation
    if (!input.userId) {
      errors.push('User ID is required');
    }

    // KYC validation
    if (input.kycStatus !== EXCHANGE_REQUIRED_KYC_LEVEL) {
      errors.push(\`KYC verification required (current: \${input.kycStatus})\`);
    }

    // Side validation
    if (!['buy', 'sell'].includes(input.side)) {
      errors.push('Invalid exchange side (must be buy or sell)');
    }

    // Amount validation
    if (input.amount < MIN_EXCHANGE_AMOUNT) {
      errors.push(\`Minimum amount is $\${MIN_EXCHANGE_AMOUNT}\`);
    }

    if (input.amount > MAX_EXCHANGE_AMOUNT) {
      errors.push(\`Maximum amount is $\${MAX_EXCHANGE_AMOUNT}\`);
    }

    // Balance validation for sell
    if (input.side === 'sell') {
      if (input.cxxrBalance === undefined) {
        errors.push('CXXR balance required for sell validation');
      } else if (input.cxxrBalance < input.amount) {
        errors.push(
          \`Insufficient CXXR balance. Required: \${input.amount}, Available: \${input.cxxrBalance}\`
        );
      }
    }

    // Daily limit validation
    const dailyTotal = (input.dailyExchangeAmount || 0) + input.amount;
    if (dailyTotal > MAX_DAILY_EXCHANGE_AMOUNT) {
      errors.push(
        \`Daily exchange limit exceeded. Limit: $\${MAX_DAILY_EXCHANGE_AMOUNT}, \` +
        \`Used: $\${input.dailyExchangeAmount || 0}\`
      );
    }

    // Monthly limit validation
    const monthlyTotal = (input.monthlyExchangeAmount || 0) + input.amount;
    if (monthlyTotal > MAX_MONTHLY_EXCHANGE_AMOUNT) {
      errors.push(
        \`Monthly exchange limit exceeded. Limit: $\${MAX_MONTHLY_EXCHANGE_AMOUNT}, \` +
        \`Used: $\${input.monthlyExchangeAmount || 0}\`
      );
    }

    return { valid: errors.length === 0, errors };
  },

  /**
   * Calculate exchange result
   */
  calculateExchange(input: ExchangeInput): ExchangeOutput {
    const validation = this.validate(input);
    if (!validation.valid) {
      throw new Error(\`Exchange validation failed: \${validation.errors.join(', ')}\`);
    }

    // Use FeeContract for fee calculation
    const feeResult = FeeContract.calculateFee({
      type: 'exchange',
      amount: input.amount,
    });

    // Use FeeContract for distribution
    const distribution = FeeContract.distributeFee(feeResult.feeAmount);

    if (input.side === 'buy') {
      // Buy: USD → CXXR (fee deducted from CXXR received)
      return {
        side: 'buy',
        inputAmount: input.amount,
        inputCurrency: 'USD',
        outputAmount: feeResult.netAmount,
        outputCurrency: 'CXXR',
        feeAmount: feeResult.feeAmount,
        feePercent: EXCHANGE_FEE,
        feeDistribution: {
          cxxr: distribution.cxxr,
          ccc: distribution.ccc,
          reserve: distribution.reserve,
        },
      };
    } else {
      // Sell: CXXR → USD (fee deducted from USD received)
      return {
        side: 'sell',
        inputAmount: input.amount,
        inputCurrency: 'CXXR',
        outputAmount: feeResult.netAmount,
        outputCurrency: 'USD',
        feeAmount: feeResult.feeAmount,
        feePercent: EXCHANGE_FEE,
        feeDistribution: {
          cxxr: distribution.cxxr,
          ccc: distribution.ccc,
          reserve: distribution.reserve,
        },
      };
    }
  },

  /**
   * Get current limits for user
   */
  getLimits(
    dailyExchangeAmount: number = 0,
    monthlyExchangeAmount: number = 0
  ): ExchangeLimits {
    return {
      min: MIN_EXCHANGE_AMOUNT,
      max: MAX_EXCHANGE_AMOUNT,
      dailyRemaining: Math.max(0, MAX_DAILY_EXCHANGE_AMOUNT - dailyExchangeAmount),
      monthlyRemaining: Math.max(0, MAX_MONTHLY_EXCHANGE_AMOUNT - monthlyExchangeAmount),
    };
  },

  /**
   * Get quote for exchange (preview without validation)
   */
  getQuote(
    side: ExchangeSide,
    amount: number
  ): {
    inputAmount: number;
    outputAmount: number;
    feeAmount: number;
    feePercent: number;
    rate: string;
  } {
    const feeResult = FeeContract.calculateFee({
      type: 'exchange',
      amount,
    });

    return {
      inputAmount: amount,
      outputAmount: feeResult.netAmount,
      feeAmount: feeResult.feeAmount,
      feePercent: EXCHANGE_FEE,
      rate: '1 CXXR = $1.00',
    };
  },
};
`,

  'contracts/exchange/SpeculationContract.ts': `// contracts/exchange/SpeculationContract.ts
// ALL RIGHTS RESERVED

/**
 * Speculation Control Contract
 *
 * Business rules to limit speculative trading that could destabilize the peg.
 *
 * Contract Reference: Section 1.1.2
 * "Control measures to limit speculative value that would destabilize the
 * token peg ratio and reserve ratio"
 *
 * Contract Reference: Section 1.1.5
 * "maintain limits on speculative trading risking the stability and security
 * of the CXXR Token"
 *
 * Controls include:
 * 1. Velocity limits (trades per hour/day)
 * 2. Large order detection
 * 3. Price deviation alerts
 * 4. Rapid buy-sell pattern detection
 *
 * @module contracts/exchange/SpeculationContract
 */

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════

/** Maximum trades per hour per user */
export const MAX_TRADES_PER_HOUR = 10;

/** Maximum trades per day per user */
export const MAX_TRADES_PER_DAY = 50;

/** Large order threshold (USD) */
export const LARGE_ORDER_THRESHOLD = 5000;

/** Large order delay in seconds */
export const LARGE_ORDER_DELAY_SECONDS = 60;

/** Rapid reversal window in minutes */
export const RAPID_REVERSAL_WINDOW_MINUTES = 5;

/** Minimum time between same-direction trades (seconds) */
export const MIN_TRADE_INTERVAL_SECONDS = 10;

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export type TradeSide = 'buy' | 'sell';

export interface TradeRecord {
  side: TradeSide;
  amount: number;
  timestamp: Date;
}

export interface VelocityCheckInput {
  userId: string;
  recentTrades: TradeRecord[];
  proposedSide: TradeSide;
  proposedAmount: number;
}

export interface VelocityCheckOutput {
  allowed: boolean;
  reason?: string;
  tradesLastHour: number;
  tradesLastDay: number;
  isLargeOrder: boolean;
  delayRequired: number; // seconds
  isRapidReversal: boolean;
}

export interface PatternAnalysis {
  isWashTrading: boolean;
  isRapidReversal: boolean;
  netPosition: number; // positive = net bought, negative = net sold
  tradeCount: number;
  volumeTraded: number;
}

// ═══════════════════════════════════════════════════════
// CONTRACT
// ═══════════════════════════════════════════════════════

export const SpeculationContract = {
  // Constants exposed for transparency
  MAX_TRADES_PER_HOUR,
  MAX_TRADES_PER_DAY,
  LARGE_ORDER_THRESHOLD,
  LARGE_ORDER_DELAY_SECONDS,
  RAPID_REVERSAL_WINDOW_MINUTES,
  MIN_TRADE_INTERVAL_SECONDS,

  /**
   * Check if trade is allowed based on velocity limits
   */
  checkVelocity(input: VelocityCheckInput): VelocityCheckOutput {
    const now = Date.now();
    const oneHourAgo = now - 60 * 60 * 1000;
    const oneDayAgo = now - 24 * 60 * 60 * 1000;
    const reversalWindow = now - RAPID_REVERSAL_WINDOW_MINUTES * 60 * 1000;

    // Count trades in time windows
    const tradesLastHour = input.recentTrades.filter(
      (t) => t.timestamp.getTime() >= oneHourAgo
    ).length;

    const tradesLastDay = input.recentTrades.filter(
      (t) => t.timestamp.getTime() >= oneDayAgo
    ).length;

    // Check for rapid reversal (buy then immediate sell or vice versa)
    const recentReversalTrades = input.recentTrades.filter(
      (t) => t.timestamp.getTime() >= reversalWindow
    );
    const oppositeSideTrades = recentReversalTrades.filter(
      (t) => t.side !== input.proposedSide
    );
    const isRapidReversal = oppositeSideTrades.length > 0;

    // Check large order
    const isLargeOrder = input.proposedAmount >= LARGE_ORDER_THRESHOLD;

    // Check minimum interval between trades
    const lastTrade = input.recentTrades[input.recentTrades.length - 1];
    let delayRequired = 0;
    if (lastTrade) {
      const secondsSinceLastTrade = (now - lastTrade.timestamp.getTime()) / 1000;
      if (secondsSinceLastTrade < MIN_TRADE_INTERVAL_SECONDS) {
        delayRequired = MIN_TRADE_INTERVAL_SECONDS - secondsSinceLastTrade;
      }
    }

    // Add delay for large orders
    if (isLargeOrder && delayRequired < LARGE_ORDER_DELAY_SECONDS) {
      delayRequired = LARGE_ORDER_DELAY_SECONDS;
    }

    // Check velocity limits
    if (tradesLastHour >= MAX_TRADES_PER_HOUR) {
      return {
        allowed: false,
        reason: \`Hourly trade limit reached (\${MAX_TRADES_PER_HOUR} trades/hour)\`,
        tradesLastHour,
        tradesLastDay,
        isLargeOrder,
        delayRequired,
        isRapidReversal,
      };
    }

    if (tradesLastDay >= MAX_TRADES_PER_DAY) {
      return {
        allowed: false,
        reason: \`Daily trade limit reached (\${MAX_TRADES_PER_DAY} trades/day)\`,
        tradesLastHour,
        tradesLastDay,
        isLargeOrder,
        delayRequired,
        isRapidReversal,
      };
    }

    // Warn but allow rapid reversals (might be legitimate)
    let reason: string | undefined;
    if (isRapidReversal) {
      reason = \`Rapid reversal detected (\${input.proposedSide} after recent \${oppositeSideTrades[0].side})\`;
    }

    return {
      allowed: true,
      reason,
      tradesLastHour,
      tradesLastDay,
      isLargeOrder,
      delayRequired: Math.ceil(delayRequired),
      isRapidReversal,
    };
  },

  /**
   * Analyze trading patterns for potential manipulation
   */
  analyzePatterns(trades: TradeRecord[], windowMinutes: number = 60): PatternAnalysis {
    const now = Date.now();
    const windowStart = now - windowMinutes * 60 * 1000;

    const windowTrades = trades.filter((t) => t.timestamp.getTime() >= windowStart);

    // Calculate net position and volume
    let netPosition = 0;
    let volumeTraded = 0;

    for (const trade of windowTrades) {
      volumeTraded += trade.amount;
      if (trade.side === 'buy') {
        netPosition += trade.amount;
      } else {
        netPosition -= trade.amount;
      }
    }

    // Detect wash trading (high volume, low net position)
    const isWashTrading =
      volumeTraded > 1000 && Math.abs(netPosition) < volumeTraded * 0.1;

    // Detect rapid reversals
    const isRapidReversal = windowTrades.length >= 2 &&
      windowTrades.some((t, i) => i > 0 && t.side !== windowTrades[i - 1].side);

    return {
      isWashTrading,
      isRapidReversal,
      netPosition: Math.round(netPosition * 100) / 100,
      tradeCount: windowTrades.length,
      volumeTraded: Math.round(volumeTraded * 100) / 100,
    };
  },

  /**
   * Get remaining trades for user
   */
  getRemainingTrades(
    tradesLastHour: number,
    tradesLastDay: number
  ): { hourly: number; daily: number } {
    return {
      hourly: Math.max(0, MAX_TRADES_PER_HOUR - tradesLastHour),
      daily: Math.max(0, MAX_TRADES_PER_DAY - tradesLastDay),
    };
  },

  /**
   * Check if amount qualifies as large order
   */
  isLargeOrder(amount: number): boolean {
    return amount >= LARGE_ORDER_THRESHOLD;
  },

  /**
   * Get required delay for order
   */
  getRequiredDelay(amount: number, lastTradeTimestamp?: Date): number {
    const now = Date.now();
    let delay = 0;

    // Minimum interval between trades
    if (lastTradeTimestamp) {
      const secondsSinceLastTrade = (now - lastTradeTimestamp.getTime()) / 1000;
      if (secondsSinceLastTrade < MIN_TRADE_INTERVAL_SECONDS) {
        delay = MIN_TRADE_INTERVAL_SECONDS - secondsSinceLastTrade;
      }
    }

    // Large order delay
    if (amount >= LARGE_ORDER_THRESHOLD) {
      delay = Math.max(delay, LARGE_ORDER_DELAY_SECONDS);
    }

    return Math.ceil(delay);
  },
};
`,

  'contracts/fees/DividendContract.ts': `// contracts/fees/DividendContract.ts
// ALL RIGHTS RESERVED

/**
 * Dividend Contract
 *
 * Business rules for reserve token (RCXXR) dividend distribution.
 *
 * Contract Reference: Section 1.1.2
 * "Create reward program / dividend program for new split CXXR reserve tokens only,
 * payable from the transaction fee reward/dividend apportioned and reserved within
 * the CXXR Treasury."
 *
 * Distribution: 5% of all transaction fees go to the dividend pool.
 * Eligible: Only RCXXR (reserve token) holders.
 * Method: Pro-rata based on RCXXR balance at claim time.
 *
 * @module contracts/fees/DividendContract
 */

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════

/** Percentage of fees allocated to dividend pool (Section 2.3.1) */
export const DIVIDEND_POOL_PERCENT = 5;

/** Minimum RCXXR balance required to claim dividends */
export const MIN_RCXXR_FOR_CLAIM = 1;

/** Minimum dividend amount to process (avoid dust transactions) */
export const MIN_DIVIDEND_AMOUNT = 0.01;

/** Claim window in days (quarterly) */
export const CLAIM_WINDOW_DAYS = 90;

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export interface DividendClaimInput {
  userId: string;
  rcxxrBalance: number;
  totalRcxxrSupply: number;
  poolBalance: number;
}

export interface DividendClaimOutput {
  eligible: boolean;
  claimAmount: number;
  rcxxrToBurn: number;
  errors: string[];
}

export interface DividendPoolStatus {
  balance: number;
  totalRcxxrSupply: number;
  pricePerRcxxr: number;
  lastDistribution: Date | null;
  nextClaimWindow: Date;
}

export interface DividendValidationResult {
  valid: boolean;
  errors: string[];
}

// ═══════════════════════════════════════════════════════
// CONTRACT
// ═══════════════════════════════════════════════════════

export const DividendContract = {
  // Constants exposed for transparency
  DIVIDEND_POOL_PERCENT,
  MIN_RCXXR_FOR_CLAIM,
  MIN_DIVIDEND_AMOUNT,
  CLAIM_WINDOW_DAYS,

  /**
   * Validate dividend claim eligibility
   */
  validate(input: DividendClaimInput): DividendValidationResult {
    const errors: string[] = [];

    if (!input.userId) {
      errors.push('User ID is required');
    }

    if (input.rcxxrBalance < MIN_RCXXR_FOR_CLAIM) {
      errors.push(\`Minimum RCXXR balance of \${MIN_RCXXR_FOR_CLAIM} required\`);
    }

    if (input.totalRcxxrSupply <= 0) {
      errors.push('Total RCXXR supply must be positive');
    }

    if (input.poolBalance <= 0) {
      errors.push('Dividend pool is empty');
    }

    return { valid: errors.length === 0, errors };
  },

  /**
   * Calculate dividend claim amount
   *
   * Formula: (userRcxxr / totalRcxxr) * poolBalance
   *
   * Note: Claiming dividends burns the user's RCXXR tokens.
   */
  calculateClaim(input: DividendClaimInput): DividendClaimOutput {
    const validation = this.validate(input);

    if (!validation.valid) {
      return {
        eligible: false,
        claimAmount: 0,
        rcxxrToBurn: 0,
        errors: validation.errors,
      };
    }

    // Pro-rata calculation
    const share = input.rcxxrBalance / input.totalRcxxrSupply;
    const claimAmount = Math.round(share * input.poolBalance * 100) / 100;

    // Check minimum dividend amount
    if (claimAmount < MIN_DIVIDEND_AMOUNT) {
      return {
        eligible: false,
        claimAmount: 0,
        rcxxrToBurn: 0,
        errors: [\`Calculated dividend (\${claimAmount}) below minimum \${MIN_DIVIDEND_AMOUNT}\`],
      };
    }

    return {
      eligible: true,
      claimAmount,
      rcxxrToBurn: input.rcxxrBalance, // Burn all RCXXR on claim
      errors: [],
    };
  },

  /**
   * Calculate fee contribution to dividend pool
   */
  calculatePoolContribution(feeAmount: number): number {
    if (feeAmount <= 0) return 0;
    return Math.round((feeAmount * DIVIDEND_POOL_PERCENT) / 100 * 100) / 100;
  },

  /**
   * Calculate price per RCXXR token
   */
  getPricePerRcxxr(poolBalance: number, totalRcxxrSupply: number): number {
    if (totalRcxxrSupply <= 0) return 0;
    return Math.round((poolBalance / totalRcxxrSupply) * 100) / 100;
  },

  /**
   * Check if claim window is open
   */
  isClaimWindowOpen(lastDistribution: Date | null): boolean {
    if (!lastDistribution) return true; // First distribution always allowed

    const now = new Date();
    const daysSinceLastDistribution = Math.floor(
      (now.getTime() - lastDistribution.getTime()) / (1000 * 60 * 60 * 24)
    );

    return daysSinceLastDistribution >= CLAIM_WINDOW_DAYS;
  },
};
`,

  'contracts/fees/FeeContract.ts': `// contracts/fees/FeeContract.ts
// ALL RIGHTS RESERVED

/**
 * Fee Contract
 *
 * Business rules for fee calculation and distribution.
 * This module serves as an auditable "smart contract".
 *
 * Contract Reference: Section 2.3.1 Fee Allocation
 * - CXXR: 47.5% operational/administrative
 * - CCC: 47.5% maintenance/infrastructure
 * - Reserve: 5% dividend program
 *
 * Fee Schedule:
 * - Minting (adding new CXXR to supply): 5%
 * - Exchange (buy/sell CXXR): 0.5%
 * - Transfer (P2P): 0.5%
 * - Withdrawal: 0.5%
 *
 * @module contracts/fees/FeeContract
 */

// ═══════════════════════════════════════════════════════
// CONSTANTS (auditable parameters from contract)
// ═══════════════════════════════════════════════════════

/** Minting fee percentage (Section 1.1.2) - for adding new CXXR to supply */
export const MINT_FEE_PERCENT = 5;

/** Exchange fee percentage - for buy/sell operations */
export const EXCHANGE_FEE_PERCENT = 0.5;

/** Transaction fee percentage (Section 1.1.2) */
export const TRANSFER_FEE_PERCENT = 0.5;

/** Withdrawal fee percentage (Section 1.1.2) */
export const WITHDRAWAL_FEE_PERCENT = 0.5;

/** Fee distribution to CXXR (Section 2.3.1) */
export const SPLIT_CXXR_PERCENT = 47.5;

/** Fee distribution to CCC (Section 2.3.1) */
export const SPLIT_CCC_PERCENT = 47.5;

/** Fee distribution to Reserve/Dividends (Section 2.3.1) */
export const SPLIT_RESERVE_PERCENT = 5;

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export type FeeType = 'mint' | 'exchange' | 'transfer' | 'withdrawal';

export interface FeeCalculationInput {
  type: FeeType;
  amount: number; // Base amount before fee
}

export interface FeeCalculationOutput {
  grossAmount: number;
  feeAmount: number;
  netAmount: number;
  feePercent: number;
}

export interface FeeDistribution {
  cxxr: number;
  ccc: number;
  reserve: number;
  total: number;
}

export interface FeeValidationResult {
  valid: boolean;
  errors: string[];
}

// ═══════════════════════════════════════════════════════
// CONTRACT
// ═══════════════════════════════════════════════════════

export const FeeContract = {
  // Constants exposed for transparency
  MINT_FEE_PERCENT,
  EXCHANGE_FEE_PERCENT,
  TRANSFER_FEE_PERCENT,
  WITHDRAWAL_FEE_PERCENT,
  SPLIT_CXXR_PERCENT,
  SPLIT_CCC_PERCENT,
  SPLIT_RESERVE_PERCENT,

  /**
   * Validate fee calculation input
   */
  validate(input: FeeCalculationInput): FeeValidationResult {
    const errors: string[] = [];

    if (!['mint', 'exchange', 'transfer', 'withdrawal'].includes(input.type)) {
      errors.push(\`Invalid fee type: \${input.type}\`);
    }

    if (typeof input.amount !== 'number' || isNaN(input.amount)) {
      errors.push('Amount must be a valid number');
    }

    if (input.amount <= 0) {
      errors.push('Amount must be greater than zero');
    }

    if (input.amount > Number.MAX_SAFE_INTEGER) {
      errors.push('Amount exceeds maximum safe integer');
    }

    return { valid: errors.length === 0, errors };
  },

  /**
   * Get fee percentage for transaction type
   */
  getFeePercent(type: FeeType): number {
    switch (type) {
      case 'mint':
        return MINT_FEE_PERCENT;
      case 'exchange':
        return EXCHANGE_FEE_PERCENT;
      case 'transfer':
        return TRANSFER_FEE_PERCENT;
      case 'withdrawal':
        return WITHDRAWAL_FEE_PERCENT;
    }
  },

  /**
   * Calculate fee for a transaction
   */
  calculateFee(input: FeeCalculationInput): FeeCalculationOutput {
    const validation = this.validate(input);
    if (!validation.valid) {
      throw new Error(\`Fee calculation failed: \${validation.errors.join(', ')}\`);
    }

    const feePercent = this.getFeePercent(input.type);
    const feeAmount = Math.round((input.amount * feePercent) / 100 * 100) / 100; // Round to 2 decimals
    const netAmount = Math.round((input.amount - feeAmount) * 100) / 100; // Round to 2 decimals

    return {
      grossAmount: input.amount,
      feeAmount,
      netAmount,
      feePercent,
    };
  },

  /**
   * Distribute collected fee to parties
   */
  distributeFee(feeAmount: number): FeeDistribution {
    if (feeAmount <= 0) {
      return { cxxr: 0, ccc: 0, reserve: 0, total: 0 };
    }

    // Calculate splits (round to 2 decimals)
    const cxxr = Math.round((feeAmount * SPLIT_CXXR_PERCENT) / 100 * 100) / 100;
    const ccc = Math.round((feeAmount * SPLIT_CCC_PERCENT) / 100 * 100) / 100;
    const reserve = Math.round((feeAmount * SPLIT_RESERVE_PERCENT) / 100 * 100) / 100;

    // Adjust for rounding (ensure total equals fee)
    const total = cxxr + ccc + reserve;
    const adjustment = Math.round((feeAmount - total) * 100) / 100;

    return {
      cxxr: cxxr + adjustment, // Any rounding adjustment goes to CXXR
      ccc,
      reserve,
      total: feeAmount,
    };
  },
};
`,

  'contracts/governance/GovernanceContract.ts': `
// contracts/governance/GovernanceContract.ts
// ALL RIGHTS RESERVED

/**
 * Governance Contract
 *
 * Business rules for governance mechanisms - "council of eight" voting.
 *
 * Contract Reference: Section 1.1.2
 * "Governance mechanisms, council of eight vote with voting and veto powers,
 * board of directors international-government relations, including voting
 * or advisory board oversight for future updates or amendments"
 *
 * @module contracts/governance/GovernanceContract
 */

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════

/** Council size per contract */
export const COUNCIL_SIZE = 8;

/** Quorum: votes required to pass (simple majority + 1) */
export const QUORUM = 5;

/** Veto threshold: vetoes required to block */
export const VETO_THRESHOLD = 3;

/** Proposal voting period in days */
export const VOTING_PERIOD_DAYS = 7;

/** Execution delay after passing (hours) */
export const EXECUTION_DELAY_HOURS = 48;

/** Minimum title length */
export const MIN_TITLE_LENGTH = 10;

/** Minimum description length */
export const MIN_DESCRIPTION_LENGTH = 50;

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export type MemberRole = 'council' | 'board' | 'advisor';

export type ProposalStatus = 
  | 'draft' 
  | 'voting' 
  | 'passed' 
  | 'rejected' 
  | 'vetoed' 
  | 'executed' 
  | 'expired';

export type VoteType = 'yes' | 'no' | 'veto' | 'abstain';

export type ProposalType =
  | 'feature_request'
  | 'parameter_change'
  | 'fee_adjustment'
  | 'member_add'
  | 'member_remove'
  | 'policy_update'
  | 'reserve_action'
  | 'compliance_update';

export interface CouncilMember {
  id: string;
  userId: string;
  role: MemberRole;
  displayName: string;
  canVeto: boolean;
  canCreateProposals: boolean;
  isActive: boolean;
}

export interface Proposal {
  id: string;
  proposalNumber: number;
  title: string;
  description: string;
  proposalType: ProposalType;
  createdBy: string;
  status: ProposalStatus;
  votingStartsAt?: Date;
  votingEndsAt?: Date;
  executableAt?: Date;
  executedAt?: Date;
  executedBy?: string;
  yesVotes: number;
  noVotes: number;
  vetoVotes: number;
  abstainVotes: number;
}

export interface Vote {
  id: string;
  proposalId: string;
  memberId: string;
  vote: VoteType;
  comment?: string;
}

export interface VoteTallies {
  yesVotes: number;
  noVotes: number;
  vetoVotes: number;
  abstainVotes: number;
}

export interface VoteResult {
  status: ProposalStatus;
  reason: string;
  canStillPass: boolean;
  votesNeeded: number;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface CanVoteResult {
  allowed: boolean;
  reason?: string;
}

export interface CanExecuteResult {
  allowed: boolean;
  reason?: string;
}

// ═══════════════════════════════════════════════════════
// CONTRACT
// ═══════════════════════════════════════════════════════

export const GovernanceContract = {
  // Expose constants for transparency
  COUNCIL_SIZE,
  QUORUM,
  VETO_THRESHOLD,
  VOTING_PERIOD_DAYS,
  EXECUTION_DELAY_HOURS,
  MIN_TITLE_LENGTH,
  MIN_DESCRIPTION_LENGTH,

  /**
   * Validate proposal fields
   */
  validateProposal(proposal: { 
    title?: string; 
    description?: string; 
    proposalType?: string;
  }): ValidationResult {
    const errors: string[] = [];

    if (!proposal.title || proposal.title.length < MIN_TITLE_LENGTH) {
      errors.push(\`Title must be at least \${MIN_TITLE_LENGTH} characters\`);
    }

    if (!proposal.description || proposal.description.length < MIN_DESCRIPTION_LENGTH) {
      errors.push(\`Description must be at least \${MIN_DESCRIPTION_LENGTH} characters\`);
    }

    const validTypes: ProposalType[] = [
      'feature_request', 'parameter_change', 'fee_adjustment', 'member_add', 'member_remove',
      'policy_update', 'reserve_action', 'compliance_update'
    ];

    if (!proposal.proposalType || !validTypes.includes(proposal.proposalType as ProposalType)) {
      errors.push(\`Invalid proposal type. Must be one of: \${validTypes.join(', ')}\`);
    }

    return { valid: errors.length === 0, errors };
  },

  /**
   * Calculate voting deadline (7 days from start)
   */
  calculateVotingDeadline(startDate: Date): Date {
    const deadline = new Date(startDate);
    deadline.setDate(deadline.getDate() + VOTING_PERIOD_DAYS);
    return deadline;
  },

  /**
   * Calculate executable date (48h after voting ends)
   */
  calculateExecutableDate(votingEndsAt: Date): Date {
    const executable = new Date(votingEndsAt);
    executable.setHours(executable.getHours() + EXECUTION_DELAY_HOURS);
    return executable;
  },

  /**
   * Calculate vote result based on tallies
   * 
   * Algorithm:
   * 1. IF veto_votes >= 3 → status = 'vetoed'
   * 2. ELSE IF yes_votes >= 5 → status = 'passed'  
   * 3. ELSE IF (8 - total_votes) + yes_votes < 5 → status = 'rejected'
   * 4. ELSE → status = 'voting' (still open)
   */
  calculateVoteResult(tallies: VoteTallies): VoteResult {
    const { yesVotes, noVotes, vetoVotes, abstainVotes } = tallies;
    const totalVotes = yesVotes + noVotes + vetoVotes + abstainVotes;
    const remainingVotes = COUNCIL_SIZE - totalVotes;

    // Rule 1: Veto threshold reached
    if (vetoVotes >= VETO_THRESHOLD) {
      return {
        status: 'vetoed',
        reason: \`Vetoed: \${vetoVotes} veto votes (threshold: \${VETO_THRESHOLD})\`,
        canStillPass: false,
        votesNeeded: 0,
      };
    }

    // Rule 2: Quorum reached
    if (yesVotes >= QUORUM) {
      return {
        status: 'passed',
        reason: \`Passed: \${yesVotes} yes votes (quorum: \${QUORUM})\`,
        canStillPass: false,
        votesNeeded: 0,
      };
    }

    // Rule 3: Mathematically impossible to pass
    const maxPossibleYes = yesVotes + remainingVotes;
    if (maxPossibleYes < QUORUM) {
      return {
        status: 'rejected',
        reason: \`Rejected: Cannot reach quorum (max possible: \${maxPossibleYes}, needed: \${QUORUM})\`,
        canStillPass: false,
        votesNeeded: 0,
      };
    }

    // Rule 4: Still voting
    const votesNeeded = QUORUM - yesVotes;
    return {
      status: 'voting',
      reason: \`Voting: \${yesVotes}/\${QUORUM} yes votes, \${votesNeeded} more needed\`,
      canStillPass: true,
      votesNeeded,
    };
  },

  /**
   * Check if member can cast a vote on proposal
   */
  canCastVote(
    member: { role: MemberRole; canVeto: boolean; isActive: boolean },
    proposal: { status: ProposalStatus },
    voteType: VoteType
  ): CanVoteResult {
    // Must be active
    if (!member.isActive) {
      return { allowed: false, reason: 'Member is not active' };
    }

    // Proposal must be in voting status
    if (proposal.status !== 'voting') {
      return { allowed: false, reason: 'Proposal is not in voting status' };
    }

    // Advisors cannot vote
    if (member.role === 'advisor') {
      return { allowed: false, reason: 'Advisors cannot vote' };
    }

    // Only council members with canVeto can cast veto
    if (voteType === 'veto' && !member.canVeto) {
      return { allowed: false, reason: 'Only council members can cast veto votes' };
    }

    return { allowed: true };
  },

  /**
   * Check if proposal can be executed
   */
  canExecute(
    proposal: { status: ProposalStatus; executableAt?: Date },
    currentTime: Date = new Date()
  ): CanExecuteResult {
    // Must be passed
    if (proposal.status !== 'passed') {
      return { allowed: false, reason: 'Proposal has not passed' };
    }

    // Must have executable date
    if (!proposal.executableAt) {
      return { allowed: false, reason: 'Executable date not set' };
    }

    // 48h delay must have elapsed
    if (currentTime < proposal.executableAt) {
      const hoursRemaining = Math.ceil(
        (proposal.executableAt.getTime() - currentTime.getTime()) / (1000 * 60 * 60)
      );
      return { 
        allowed: false, 
        reason: \`Execution delay not elapsed (\${hoursRemaining}h remaining)\` 
      };
    }

    return { allowed: true };
  },

  /**
   * Check if member can create proposals
   */
  canCreateProposal(member: { 
    role: MemberRole; 
    canCreateProposals: boolean; 
    isActive: boolean;
  }): CanVoteResult {
    if (!member.isActive) {
      return { allowed: false, reason: 'Member is not active' };
    }

    if (!member.canCreateProposals) {
      return { allowed: false, reason: 'Member cannot create proposals' };
    }

    if (member.role === 'advisor') {
      return { allowed: false, reason: 'Advisors cannot create proposals' };
    }

    return { allowed: true };
  },

  /**
   * Format vote display string (e.g., "3/1/0/2")
   * Format: yes/no/veto/abstain
   */
  formatVoteTally(tallies: VoteTallies): string {
    return \`\${tallies.yesVotes}/\${tallies.noVotes}/\${tallies.vetoVotes}/\${tallies.abstainVotes}\`;
  },

  /**
   * Check if voting period has expired
   */
  isVotingExpired(votingEndsAt: Date, currentTime: Date = new Date()): boolean {
    return currentTime > votingEndsAt;
  },
};

`,

  'contracts/reserve/PegContract.ts': `// contracts/reserve/PegContract.ts
// ALL RIGHTS RESERVED

/**
 * Peg Contract
 *
 * Business rules for maintaining the 1:1 USD peg.
 *
 * Contract Reference: Section 1.1.2
 * "Enforcement of the 1:1 peg to USD"
 *
 * Contract Reference: Section 1.1.5
 * "maintain 1:1 USD peg ratios"
 *
 * The peg is maintained through:
 * 1. All internal operations settle at exactly $1.00
 * 2. CEX price monitoring with alerts on deviation
 * 3. Token split mechanism when price exceeds threshold
 *
 * @module contracts/reserve/PegContract
 */

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════

/** Target peg price */
export const PEG_PRICE = 1.0;

/** Normal deviation tolerance (0.5%) */
export const NORMAL_DEVIATION_PERCENT = 0.5;

/** Warning deviation threshold (1%) */
export const WARNING_DEVIATION_PERCENT = 1.0;

/** Critical deviation threshold (2%) */
export const CRITICAL_DEVIATION_PERCENT = 2.0;

/** Split trigger threshold (5%) */
export const SPLIT_TRIGGER_PERCENT = 5.0;

/** Price check interval in minutes */
export const PRICE_CHECK_INTERVAL_MINUTES = 5;

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export type PegStatus = 'stable' | 'warning' | 'critical' | 'split_trigger';
export type DeviationDirection = 'above' | 'below' | 'stable';

export interface PriceInput {
  price: number;
  source: string;
  timestamp: Date;
}

export interface PegCheckOutput {
  price: number;
  pegPrice: number;
  deviationPercent: number;
  direction: DeviationDirection;
  status: PegStatus;
  shouldTriggerSplit: boolean;
  alerts: string[];
}

export interface PegValidationResult {
  valid: boolean;
  status: PegStatus;
  errors: string[];
  warnings: string[];
}

// ═══════════════════════════════════════════════════════
// CONTRACT
// ═══════════════════════════════════════════════════════

export const PegContract = {
  // Constants exposed for transparency
  PEG_PRICE,
  NORMAL_DEVIATION_PERCENT,
  WARNING_DEVIATION_PERCENT,
  CRITICAL_DEVIATION_PERCENT,
  SPLIT_TRIGGER_PERCENT,
  PRICE_CHECK_INTERVAL_MINUTES,

  /**
   * Check current peg status
   */
  checkPeg(input: PriceInput): PegCheckOutput {
    const deviation = input.price - PEG_PRICE;
    const deviationPercent = Math.abs(deviation) * 100;
    const alerts: string[] = [];

    // Determine direction
    let direction: DeviationDirection;
    if (Math.abs(deviation) <= PEG_PRICE * (NORMAL_DEVIATION_PERCENT / 100)) {
      direction = 'stable';
    } else if (deviation > 0) {
      direction = 'above';
    } else {
      direction = 'below';
    }

    // Determine status
    let status: PegStatus;
    let shouldTriggerSplit = false;

    if (deviationPercent >= SPLIT_TRIGGER_PERCENT) {
      status = 'split_trigger';
      shouldTriggerSplit = direction === 'above'; // Only split when above peg
      alerts.push(
        \`CRITICAL: Price deviation \${deviationPercent.toFixed(2)}% \${direction} peg. \` +
        \`\${shouldTriggerSplit ? 'Token split may be triggered.' : 'Monitor closely.'}\`
      );
    } else if (deviationPercent >= CRITICAL_DEVIATION_PERCENT) {
      status = 'critical';
      alerts.push(
        \`ALERT: Price deviation \${deviationPercent.toFixed(2)}% \${direction} peg. \` +
        \`Immediate attention required.\`
      );
    } else if (deviationPercent >= WARNING_DEVIATION_PERCENT) {
      status = 'warning';
      alerts.push(
        \`WARNING: Price deviation \${deviationPercent.toFixed(2)}% \${direction} peg.\`
      );
    } else {
      status = 'stable';
    }

    return {
      price: input.price,
      pegPrice: PEG_PRICE,
      deviationPercent: Math.round(deviationPercent * 100) / 100,
      direction,
      status,
      shouldTriggerSplit,
      alerts,
    };
  },

  /**
   * Validate price is within acceptable range for operations
   */
  validate(input: PriceInput): PegValidationResult {
    const check = this.checkPeg(input);
    const errors: string[] = [];
    const warnings: string[] = [];

    if (check.status === 'critical' || check.status === 'split_trigger') {
      errors.push(...check.alerts);
    } else if (check.status === 'warning') {
      warnings.push(...check.alerts);
    }

    return {
      valid: check.status !== 'split_trigger',
      status: check.status,
      errors,
      warnings,
    };
  },

  /**
   * Get internal settlement price (always $1.00)
   */
  getSettlementPrice(): number {
    return PEG_PRICE;
  },

  /**
   * Calculate arbitrage opportunity
   */
  calculateArbitrageOpportunity(
    cexPrice: number,
    feePercent: number = 0.5
  ): {
    profitable: boolean;
    direction: 'buy' | 'sell' | 'none';
    profitPercent: number;
  } {
    const feeDecimal = feePercent / 100;
    const deviation = cexPrice - PEG_PRICE;
    const deviationPercent = Math.abs(deviation) * 100;

    // Profit must exceed fees on both legs (buy + sell)
    const totalFees = feeDecimal * 2; // Approximate
    const minProfitableDeviation = totalFees * 100;

    if (deviationPercent <= minProfitableDeviation) {
      return { profitable: false, direction: 'none', profitPercent: 0 };
    }

    // If CEX price > $1: buy CXXR internally, sell on CEX
    // If CEX price < $1: buy CXXR on CEX, redeem internally
    const direction = deviation > 0 ? 'sell' : 'buy';
    const profitPercent = deviationPercent - minProfitableDeviation;

    return {
      profitable: true,
      direction,
      profitPercent: Math.round(profitPercent * 100) / 100,
    };
  },

  /**
   * Get deviation thresholds for monitoring
   */
  getThresholds(): {
    normal: { min: number; max: number };
    warning: { min: number; max: number };
    critical: { min: number; max: number };
  } {
    return {
      normal: {
        min: PEG_PRICE * (1 - NORMAL_DEVIATION_PERCENT / 100),
        max: PEG_PRICE * (1 + NORMAL_DEVIATION_PERCENT / 100),
      },
      warning: {
        min: PEG_PRICE * (1 - WARNING_DEVIATION_PERCENT / 100),
        max: PEG_PRICE * (1 + WARNING_DEVIATION_PERCENT / 100),
      },
      critical: {
        min: PEG_PRICE * (1 - CRITICAL_DEVIATION_PERCENT / 100),
        max: PEG_PRICE * (1 + CRITICAL_DEVIATION_PERCENT / 100),
      },
    };
  },
};
`,

  'contracts/reserve/ReserveContract.ts': `// contracts/reserve/ReserveContract.ts
// ALL RIGHTS RESERVED

/**
 * Reserve Contract
 *
 * Business rules for maintaining the 2:1 reserve ratio.
 *
 * Contract Reference: Section 1.1.2
 * "Enforcement of the 2:1 reserve ratio"
 *
 * Contract Reference: Section 1.1.5
 * "Development of a transparent reserve management system integrated with smart
 * contracts to maintain the 2:1 reserve ratio"
 *
 * The 2:1 reserve ratio means:
 * - For every $1 of CXXR in circulation
 * - There must be $2 in reserve assets
 *
 * @module contracts/reserve/ReserveContract
 */

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════

/** Required reserve ratio (2:1) */
export const REQUIRED_RESERVE_RATIO = 2.0;

/** Warning threshold (1.8:1) */
export const WARNING_RESERVE_RATIO = 1.8;

/** Critical threshold (1.5:1) - halt minting */
export const CRITICAL_RESERVE_RATIO = 1.5;

/** Minimum reserve check interval in hours */
export const RESERVE_CHECK_INTERVAL_HOURS = 1;

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export type ReserveStatus = 'healthy' | 'warning' | 'critical' | 'unknown';

export interface ReserveInput {
  totalCxxrSupply: number;
  reserveBalanceUsd: number;
}

export interface ReserveRatioOutput {
  ratio: number;
  status: ReserveStatus;
  totalCxxrSupply: number;
  reserveBalanceUsd: number;
  requiredReserve: number;
  surplusOrDeficit: number;
}

export interface ReserveValidationResult {
  valid: boolean;
  canMint: boolean;
  canBurn: boolean;
  errors: string[];
  warnings: string[];
}

export interface MintImpactInput {
  currentSupply: number;
  currentReserve: number;
  mintAmount: number;
  depositAmount: number;
}

export interface MintImpactOutput {
  newSupply: number;
  newReserve: number;
  newRatio: number;
  allowed: boolean;
  reason?: string;
}

// ═══════════════════════════════════════════════════════
// CONTRACT
// ═══════════════════════════════════════════════════════

export const ReserveContract = {
  // Constants exposed for transparency
  REQUIRED_RESERVE_RATIO,
  WARNING_RESERVE_RATIO,
  CRITICAL_RESERVE_RATIO,
  RESERVE_CHECK_INTERVAL_HOURS,

  /**
   * Calculate current reserve ratio
   */
  calculateRatio(input: ReserveInput): ReserveRatioOutput {
    if (input.totalCxxrSupply <= 0) {
      return {
        ratio: Infinity,
        status: 'healthy',
        totalCxxrSupply: 0,
        reserveBalanceUsd: input.reserveBalanceUsd,
        requiredReserve: 0,
        surplusOrDeficit: input.reserveBalanceUsd,
      };
    }

    const ratio = input.reserveBalanceUsd / input.totalCxxrSupply;
    const requiredReserve = input.totalCxxrSupply * REQUIRED_RESERVE_RATIO;
    const surplusOrDeficit = input.reserveBalanceUsd - requiredReserve;

    let status: ReserveStatus;
    if (ratio >= REQUIRED_RESERVE_RATIO) {
      status = 'healthy';
    } else if (ratio >= WARNING_RESERVE_RATIO) {
      status = 'warning';
    } else if (ratio >= CRITICAL_RESERVE_RATIO) {
      status = 'critical';
    } else {
      status = 'critical';
    }

    return {
      ratio: Math.round(ratio * 1000) / 1000,
      status,
      totalCxxrSupply: input.totalCxxrSupply,
      reserveBalanceUsd: input.reserveBalanceUsd,
      requiredReserve,
      surplusOrDeficit: Math.round(surplusOrDeficit * 100) / 100,
    };
  },

  /**
   * Validate reserve status
   */
  validate(input: ReserveInput): ReserveValidationResult {
    const ratioResult = this.calculateRatio(input);
    const errors: string[] = [];
    const warnings: string[] = [];

    let canMint = true;
    let canBurn = true;

    if (ratioResult.status === 'critical') {
      canMint = false;
      errors.push(
        \`Reserve ratio (\${ratioResult.ratio}:1) below critical threshold \` +
        \`(\${CRITICAL_RESERVE_RATIO}:1). Minting suspended.\`
      );
    }

    if (ratioResult.status === 'warning') {
      warnings.push(
        \`Reserve ratio (\${ratioResult.ratio}:1) below target (\${REQUIRED_RESERVE_RATIO}:1). \` +
        \`Deficit: $\${Math.abs(ratioResult.surplusOrDeficit).toFixed(2)}\`
      );
    }

    return {
      valid: ratioResult.status !== 'critical',
      canMint,
      canBurn,
      errors,
      warnings,
    };
  },

  /**
   * Calculate impact of minting on reserve ratio
   */
  calculateMintImpact(input: MintImpactInput): MintImpactOutput {
    const newSupply = input.currentSupply + input.mintAmount;
    const newReserve = input.currentReserve + input.depositAmount;
    const newRatio = newReserve / newSupply;

    const allowed = newRatio >= CRITICAL_RESERVE_RATIO;

    return {
      newSupply,
      newReserve,
      newRatio: Math.round(newRatio * 1000) / 1000,
      allowed,
      reason: allowed
        ? undefined
        : \`Mint would reduce ratio to \${newRatio.toFixed(3)}:1 (below \${CRITICAL_RESERVE_RATIO}:1)\`,
    };
  },

  /**
   * Calculate how much can be minted while maintaining ratio
   */
  getMaxMintAmount(currentSupply: number, currentReserve: number): number {
    // Solve for: (currentReserve + deposit) / (currentSupply + mint) >= CRITICAL_RESERVE_RATIO
    // Assuming deposit = mint (1:1 backing)
    // mint <= (currentReserve - CRITICAL_RESERVE_RATIO * currentSupply) / (CRITICAL_RESERVE_RATIO - 1)

    const numerator = currentReserve - CRITICAL_RESERVE_RATIO * currentSupply;
    const denominator = CRITICAL_RESERVE_RATIO - 1;

    if (denominator <= 0) return 0;

    const maxMint = numerator / denominator;
    return Math.max(0, Math.floor(maxMint * 100) / 100);
  },

  /**
   * Calculate additional reserve needed to reach target ratio
   */
  getRequiredDeposit(currentSupply: number, currentReserve: number): number {
    const targetReserve = currentSupply * REQUIRED_RESERVE_RATIO;
    const deficit = targetReserve - currentReserve;
    return Math.max(0, Math.round(deficit * 100) / 100);
  },
};
`,

  'contracts/reserve/ReserveTokenContract.ts': `// contracts/reserve/ReserveTokenContract.ts
// ALL RIGHTS RESERVED

/**
 * Reserve Token Contract
 *
 * Business rules for RCXXR (Reserve CXXR) token.
 *
 * Contract Reference: Section 1.1.2
 * "one (1) reserve Token, a non-tradable token directly tied to the reserve,
 * preserving the 2:1 reserve ratio"
 *
 * RCXXR is:
 * - Non-transferable (cannot be sent P2P)
 * - Minted during token splits (1:1 with CXXR balance at split time)
 * - Burnable only via dividend claims
 * - Represents a claim on the reserve dividend pool
 *
 * @module contracts/reserve/ReserveTokenContract
 */

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════

/** Account type identifier for reserve tokens */
export const RCXXR_ACCOUNT_TYPE = 'rcxxr';

/** Whether transfers are allowed (always false) */
export const TRANSFERS_ALLOWED = false;

/** Minimum balance to display (avoid dust) */
export const MIN_DISPLAY_BALANCE = 0.01;

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export interface RcxxrMintInput {
  userId: string;
  cxxrBalanceAtSplit: number;
  splitId: string;
}

export interface RcxxrMintOutput {
  userId: string;
  rcxxrAmount: number;
  splitId: string;
}

export interface RcxxrBurnInput {
  userId: string;
  rcxxrBalance: number;
  claimId: string;
}

export interface RcxxrBurnOutput {
  userId: string;
  rcxxrBurned: number;
  claimId: string;
}

export interface RcxxrTransferValidationResult {
  allowed: false;
  reason: string;
}

// ═══════════════════════════════════════════════════════
// CONTRACT
// ═══════════════════════════════════════════════════════

export const ReserveTokenContract = {
  // Constants exposed for transparency
  RCXXR_ACCOUNT_TYPE,
  TRANSFERS_ALLOWED,
  MIN_DISPLAY_BALANCE,

  /**
   * Validate transfer request (always denied)
   *
   * RCXXR is non-transferable per contract requirements.
   */
  validateTransfer(): RcxxrTransferValidationResult {
    return {
      allowed: false,
      reason: 'RCXXR (Reserve Token) is non-transferable per contract Section 1.1.2',
    };
  },

  /**
   * Calculate RCXXR to mint during split
   *
   * Each user receives RCXXR equal to their CXXR balance at split time.
   */
  calculateMint(input: RcxxrMintInput): RcxxrMintOutput {
    if (!input.userId) {
      throw new Error('User ID is required');
    }

    if (input.cxxrBalanceAtSplit <= 0) {
      throw new Error('CXXR balance must be positive');
    }

    return {
      userId: input.userId,
      rcxxrAmount: input.cxxrBalanceAtSplit, // 1:1 with CXXR balance
      splitId: input.splitId,
    };
  },

  /**
   * Calculate RCXXR to burn during dividend claim
   *
   * All RCXXR is burned when claiming dividends.
   */
  calculateBurn(input: RcxxrBurnInput): RcxxrBurnOutput {
    if (!input.userId) {
      throw new Error('User ID is required');
    }

    if (input.rcxxrBalance <= 0) {
      throw new Error('No RCXXR balance to burn');
    }

    return {
      userId: input.userId,
      rcxxrBurned: input.rcxxrBalance, // Burn all on claim
      claimId: input.claimId,
    };
  },

  /**
   * Check if account type is reserve token
   */
  isReserveToken(accountType: string): boolean {
    return accountType === RCXXR_ACCOUNT_TYPE;
  },

  /**
   * Get display balance (filter dust)
   */
  getDisplayBalance(balance: number): number {
    if (balance < MIN_DISPLAY_BALANCE) {
      return 0;
    }
    return Math.round(balance * 100) / 100;
  },

  /**
   * Get token properties for display
   */
  getTokenProperties(): {
    symbol: string;
    name: string;
    decimals: number;
    transferable: boolean;
    description: string;
  } {
    return {
      symbol: 'RCXXR',
      name: 'CXXR Reserve Token',
      decimals: 2,
      transferable: false,
      description:
        'Non-transferable token representing a claim on the CXXR reserve dividend pool. ' +
        'Earned through token splits when CXXR price exceeds peg threshold.',
    };
  },
};
`,

  'contracts/token/BurnContract.ts': `// contracts/token/BurnContract.ts
// ALL RIGHTS RESERVED

/**
 * Burn Contract
 *
 * Business rules for CXXR token burning (redemption).
 *
 * Burning removes CXXR from circulation in exchange for USD withdrawal.
 * A 0.5% fee is collected on each burn/withdrawal transaction.
 *
 * @module contracts/token/BurnContract
 */

import { FeeContract } from '../fees/FeeContract.js';

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════

/** Minimum burn amount in CXXR */
export const MIN_BURN_AMOUNT = 10;

/** Maximum burn amount per transaction in CXXR */
export const MAX_BURN_AMOUNT = 50000;

/** Maximum daily burn amount per user in CXXR */
export const MAX_DAILY_BURN_AMOUNT = 100000;

/** Maximum monthly burn amount per user in CXXR */
export const MAX_MONTHLY_BURN_AMOUNT = 500000;

/** Required KYC level for burning/withdrawal */
export const BURN_REQUIRED_KYC_LEVEL = 'verified';

/** Minimum bank account age in days before withdrawal allowed */
export const MIN_BANK_ACCOUNT_AGE_DAYS = 3;

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export type BurnKycStatus = 'none' | 'pending' | 'verified' | 'rejected';

export interface BurnInput {
  userId: string;
  amountCxxr: number;
  kycStatus: BurnKycStatus;
  cxxrBalance: number;
  bankAccountLinkedDate?: Date;
  dailyBurnedAmount?: number;
  monthlyBurnedAmount?: number;
}

export interface BurnOutput {
  amountCxxr: number;
  feeAmount: number;
  netUsd: number;
  feeDistribution: {
    cxxr: number;
    ccc: number;
    reserve: number;
  };
}

export interface BurnValidationResult {
  valid: boolean;
  errors: string[];
}

// ═══════════════════════════════════════════════════════
// CONTRACT
// ═══════════════════════════════════════════════════════

export const BurnContract = {
  // Constants exposed for transparency
  MIN_BURN_AMOUNT,
  MAX_BURN_AMOUNT,
  MAX_DAILY_BURN_AMOUNT,
  MAX_MONTHLY_BURN_AMOUNT,
  BURN_REQUIRED_KYC_LEVEL,
  MIN_BANK_ACCOUNT_AGE_DAYS,

  /**
   * Validate burn operation
   */
  validate(input: BurnInput): BurnValidationResult {
    const errors: string[] = [];

    // User validation
    if (!input.userId) {
      errors.push('User ID is required');
    }

    // KYC validation
    if (input.kycStatus !== BURN_REQUIRED_KYC_LEVEL) {
      errors.push(\`KYC verification required (current: \${input.kycStatus})\`);
    }

    // Amount validation
    if (input.amountCxxr < MIN_BURN_AMOUNT) {
      errors.push(\`Minimum withdrawal amount is \${MIN_BURN_AMOUNT} CXXR\`);
    }

    if (input.amountCxxr > MAX_BURN_AMOUNT) {
      errors.push(\`Maximum withdrawal amount is \${MAX_BURN_AMOUNT} CXXR\`);
    }

    // Balance validation
    if (input.cxxrBalance < input.amountCxxr) {
      errors.push(
        \`Insufficient balance. Required: \${input.amountCxxr} CXXR, Available: \${input.cxxrBalance} CXXR\`
      );
    }

    // Bank account age validation
    if (input.bankAccountLinkedDate) {
      const daysSinceLinked = Math.floor(
        (Date.now() - input.bankAccountLinkedDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysSinceLinked < MIN_BANK_ACCOUNT_AGE_DAYS) {
        errors.push(
          \`Bank account must be linked for at least \${MIN_BANK_ACCOUNT_AGE_DAYS} days. \` +
          \`Current: \${daysSinceLinked} days\`
        );
      }
    } else {
      errors.push('Bank account must be linked before withdrawal');
    }

    // Daily limit validation
    const dailyTotal = (input.dailyBurnedAmount || 0) + input.amountCxxr;
    if (dailyTotal > MAX_DAILY_BURN_AMOUNT) {
      errors.push(
        \`Daily withdrawal limit exceeded. Limit: \${MAX_DAILY_BURN_AMOUNT} CXXR, \` +
        \`Already withdrawn: \${input.dailyBurnedAmount || 0} CXXR\`
      );
    }

    // Monthly limit validation
    const monthlyTotal = (input.monthlyBurnedAmount || 0) + input.amountCxxr;
    if (monthlyTotal > MAX_MONTHLY_BURN_AMOUNT) {
      errors.push(
        \`Monthly withdrawal limit exceeded. Limit: \${MAX_MONTHLY_BURN_AMOUNT} CXXR, \` +
        \`Already withdrawn: \${input.monthlyBurnedAmount || 0} CXXR\`
      );
    }

    return { valid: errors.length === 0, errors };
  },

  /**
   * Calculate burn result
   *
   * CXXR → USD conversion with 0.5% fee
   * Net USD = CXXR amount - 0.5% fee (at 1:1 peg)
   */
  calculateBurn(input: BurnInput): BurnOutput {
    const validation = this.validate(input);
    if (!validation.valid) {
      throw new Error(\`Burn validation failed: \${validation.errors.join(', ')}\`);
    }

    // Use FeeContract for fee calculation (withdrawal type)
    const feeResult = FeeContract.calculateFee({
      type: 'withdrawal',
      amount: input.amountCxxr,
    });

    // Use FeeContract for distribution
    const distribution = FeeContract.distributeFee(feeResult.feeAmount);

    return {
      amountCxxr: input.amountCxxr,
      feeAmount: feeResult.feeAmount,
      netUsd: feeResult.netAmount, // 1:1 peg, so net CXXR = net USD
      feeDistribution: {
        cxxr: distribution.cxxr,
        ccc: distribution.ccc,
        reserve: distribution.reserve,
      },
    };
  },

  /**
   * Check if user can burn given amount
   */
  canBurn(input: BurnInput): { allowed: boolean; reason?: string } {
    const validation = this.validate(input);
    if (!validation.valid) {
      return { allowed: false, reason: validation.errors[0] };
    }
    return { allowed: true };
  },

  /**
   * Get remaining daily limit for user
   */
  getRemainingDailyLimit(dailyBurnedAmount: number): number {
    return Math.max(0, MAX_DAILY_BURN_AMOUNT - dailyBurnedAmount);
  },

  /**
   * Get remaining monthly limit for user
   */
  getRemainingMonthlyLimit(monthlyBurnedAmount: number): number {
    return Math.max(0, MAX_MONTHLY_BURN_AMOUNT - monthlyBurnedAmount);
  },
};
`,

  'contracts/token/MintContract.ts': `// contracts/token/MintContract.ts
// ALL RIGHTS RESERVED

/**
 * Mint Contract
 *
 * Business rules for CXXR token minting (issuance).
 *
 * Contract Reference: Section 1.1.2
 * - "Token issuance and management"
 * - "Collection of minting fees (5% per transaction)"
 *
 * Minting creates new CXXR tokens in exchange for USD deposits.
 * A 5% fee is collected on each mint transaction.
 *
 * @module contracts/token/MintContract
 */

import { FeeContract } from '../fees/FeeContract.js';

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════

/** Minimum mint amount in USD */
export const MIN_MINT_AMOUNT = 10;

/** Maximum mint amount per transaction in USD */
export const MAX_MINT_AMOUNT = 100000;

/** Maximum daily mint amount per user in USD */
export const MAX_DAILY_MINT_AMOUNT = 500000;

/** Maximum monthly mint amount per user in USD */
export const MAX_MONTHLY_MINT_AMOUNT = 2000000;

/** Required KYC level for minting */
export const MINT_REQUIRED_KYC_LEVEL = 'verified';

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export type MintKycStatus = 'none' | 'pending' | 'verified' | 'rejected';

export interface MintInput {
  userId: string;
  amountUsd: number;
  kycStatus: MintKycStatus;
  dailyMintedAmount?: number;
  monthlyMintedAmount?: number;
}

export interface MintOutput {
  amountUsd: number;
  feeAmount: number;
  netCxxr: number;
  feeDistribution: {
    cxxr: number;
    ccc: number;
    reserve: number;
  };
}

export interface MintValidationResult {
  valid: boolean;
  errors: string[];
}

// ═══════════════════════════════════════════════════════
// CONTRACT
// ═══════════════════════════════════════════════════════

export const MintContract = {
  // Constants exposed for transparency
  MIN_MINT_AMOUNT,
  MAX_MINT_AMOUNT,
  MAX_DAILY_MINT_AMOUNT,
  MAX_MONTHLY_MINT_AMOUNT,
  MINT_REQUIRED_KYC_LEVEL,

  /**
   * Validate mint operation
   */
  validate(input: MintInput): MintValidationResult {
    const errors: string[] = [];

    // User validation
    if (!input.userId) {
      errors.push('User ID is required');
    }

    // KYC validation
    if (input.kycStatus !== MINT_REQUIRED_KYC_LEVEL) {
      errors.push(\`KYC verification required (current: \${input.kycStatus})\`);
    }

    // Amount validation
    if (input.amountUsd < MIN_MINT_AMOUNT) {
      errors.push(\`Minimum mint amount is $\${MIN_MINT_AMOUNT}\`);
    }

    if (input.amountUsd > MAX_MINT_AMOUNT) {
      errors.push(\`Maximum mint amount is $\${MAX_MINT_AMOUNT}\`);
    }

    // Daily limit validation
    const dailyTotal = (input.dailyMintedAmount || 0) + input.amountUsd;
    if (dailyTotal > MAX_DAILY_MINT_AMOUNT) {
      errors.push(
        \`Daily mint limit exceeded. Limit: $\${MAX_DAILY_MINT_AMOUNT}, \` +
        \`Already minted: $\${input.dailyMintedAmount || 0}\`
      );
    }

    // Monthly limit validation
    const monthlyTotal = (input.monthlyMintedAmount || 0) + input.amountUsd;
    if (monthlyTotal > MAX_MONTHLY_MINT_AMOUNT) {
      errors.push(
        \`Monthly mint limit exceeded. Limit: $\${MAX_MONTHLY_MINT_AMOUNT}, \` +
        \`Already minted: $\${input.monthlyMintedAmount || 0}\`
      );
    }

    return { valid: errors.length === 0, errors };
  },

  /**
   * Calculate mint result
   *
   * USD → CXXR conversion with 5% fee
   * Net CXXR = USD amount - 5% fee (at 1:1 peg)
   */
  calculateMint(input: MintInput): MintOutput {
    const validation = this.validate(input);
    if (!validation.valid) {
      throw new Error(\`Mint validation failed: \${validation.errors.join(', ')}\`);
    }

    // Use FeeContract for fee calculation
    const feeResult = FeeContract.calculateFee({
      type: 'mint',
      amount: input.amountUsd,
    });

    // Use FeeContract for distribution
    const distribution = FeeContract.distributeFee(feeResult.feeAmount);

    return {
      amountUsd: input.amountUsd,
      feeAmount: feeResult.feeAmount,
      netCxxr: feeResult.netAmount, // 1:1 peg, so net USD = net CXXR
      feeDistribution: {
        cxxr: distribution.cxxr,
        ccc: distribution.ccc,
        reserve: distribution.reserve,
      },
    };
  },

  /**
   * Check if user can mint given amount
   */
  canMint(input: MintInput): { allowed: boolean; reason?: string } {
    const validation = this.validate(input);
    if (!validation.valid) {
      return { allowed: false, reason: validation.errors[0] };
    }
    return { allowed: true };
  },

  /**
   * Get remaining daily limit for user
   */
  getRemainingDailyLimit(dailyMintedAmount: number): number {
    return Math.max(0, MAX_DAILY_MINT_AMOUNT - dailyMintedAmount);
  },

  /**
   * Get remaining monthly limit for user
   */
  getRemainingMonthlyLimit(monthlyMintedAmount: number): number {
    return Math.max(0, MAX_MONTHLY_MINT_AMOUNT - monthlyMintedAmount);
  },
};
`,

  'contracts/token/SplitContract.ts': `// contracts/token/SplitContract.ts
// ALL RIGHTS RESERVED

/**
 * Token Split Contract
 *
 * Business rules for the automatic token split mechanism.
 *
 * Contract Reference: Section 1.1.2
 * "Create automatic Token Split Mechanism, Trigger conditions when Token market
 * price exceeds the 1:1 peg (e.g. $3.05), the smart contract triggers an automatic
 * token split, for each token held by the user two new split tokens will be issued,
 * one (1) tradable token (CXXR), represents a tradable asset, maintaining the 1:1 peg,
 * and one (1) reserve Token, a non-tradable token directly tied to the reserve,
 * preserving the 2:1 reserve ratio."
 *
 * Implementation Note:
 * The contract mentions $3.05 as an example threshold, but this represents a 205%
 * premium which would indicate severe market dysfunction. We implement a configurable
 * threshold with a recommended default of $1.05 (5% premium) to prevent destabilization.
 *
 * @module contracts/token/SplitContract
 */

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════

/** Default price threshold to trigger split (5% above peg) */
export const DEFAULT_SPLIT_THRESHOLD_PRICE = 1.05;

/** Duration price must remain above threshold before split (hours) */
export const SPLIT_TRIGGER_DURATION_HOURS = 24;

/** Minimum time between splits (days) */
export const MIN_SPLIT_INTERVAL_DAYS = 30;

/** Split ratio: for each CXXR, user receives 1 CXXR + 1 RCXXR */
export const SPLIT_RATIO = 1;

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export interface PriceData {
  price: number;
  timestamp: Date;
  source: string;
}

export interface SplitTriggerInput {
  currentPrice: number;
  priceHistory: PriceData[]; // Last 24-48 hours
  lastSplitDate: Date | null;
  thresholdPrice?: number;
}

export interface SplitTriggerOutput {
  shouldSplit: boolean;
  reason: string;
  averagePrice: number;
  hoursAboveThreshold: number;
}

export interface SplitExecutionInput {
  userId: string;
  cxxrBalance: number;
}

export interface SplitExecutionOutput {
  newCxxrBalance: number;
  newRcxxrBalance: number;
  splitRatio: number;
}

export interface SplitValidationResult {
  valid: boolean;
  errors: string[];
}

// ═══════════════════════════════════════════════════════
// CONTRACT
// ═══════════════════════════════════════════════════════

export const SplitContract = {
  // Constants exposed for transparency
  DEFAULT_SPLIT_THRESHOLD_PRICE,
  SPLIT_TRIGGER_DURATION_HOURS,
  MIN_SPLIT_INTERVAL_DAYS,
  SPLIT_RATIO,

  /**
   * Check if conditions warrant a token split
   */
  checkTrigger(input: SplitTriggerInput): SplitTriggerOutput {
    const threshold = input.thresholdPrice || DEFAULT_SPLIT_THRESHOLD_PRICE;

    // Check minimum interval since last split
    if (input.lastSplitDate) {
      const daysSinceLastSplit = Math.floor(
        (Date.now() - input.lastSplitDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysSinceLastSplit < MIN_SPLIT_INTERVAL_DAYS) {
        return {
          shouldSplit: false,
          reason: \`Minimum interval not met (\${daysSinceLastSplit}/\${MIN_SPLIT_INTERVAL_DAYS} days)\`,
          averagePrice: input.currentPrice,
          hoursAboveThreshold: 0,
        };
      }
    }

    // Calculate average price over trigger duration
    const now = Date.now();
    const triggerWindowMs = SPLIT_TRIGGER_DURATION_HOURS * 60 * 60 * 1000;
    const recentPrices = input.priceHistory.filter(
      (p) => now - p.timestamp.getTime() <= triggerWindowMs
    );

    if (recentPrices.length === 0) {
      return {
        shouldSplit: false,
        reason: 'Insufficient price history',
        averagePrice: input.currentPrice,
        hoursAboveThreshold: 0,
      };
    }

    // Calculate weighted average (more recent = higher weight)
    const totalWeight = recentPrices.reduce((sum, _, i) => sum + (i + 1), 0);
    const weightedSum = recentPrices.reduce(
      (sum, p, i) => sum + p.price * (i + 1),
      0
    );
    const averagePrice = weightedSum / totalWeight;

    // Count consecutive hours above threshold
    const pricesAboveThreshold = recentPrices.filter((p) => p.price >= threshold);
    const hoursAboveThreshold = pricesAboveThreshold.length; // Assuming hourly data

    // Check if split should trigger
    const shouldSplit =
      averagePrice >= threshold &&
      hoursAboveThreshold >= SPLIT_TRIGGER_DURATION_HOURS;

    return {
      shouldSplit,
      reason: shouldSplit
        ? \`Price above threshold for \${hoursAboveThreshold} hours\`
        : \`Price (\${averagePrice.toFixed(4)}) below threshold (\${threshold}) or duration not met\`,
      averagePrice: Math.round(averagePrice * 10000) / 10000,
      hoursAboveThreshold,
    };
  },

  /**
   * Calculate split result for a user
   *
   * For each CXXR held, user receives:
   * - 1 CXXR (tradable, maintains peg)
   * - 1 RCXXR (reserve token, non-tradable)
   */
  calculateSplit(input: SplitExecutionInput): SplitExecutionOutput {
    // CXXR balance stays the same (maintains peg value)
    // New RCXXR minted 1:1 with CXXR balance
    return {
      newCxxrBalance: input.cxxrBalance,
      newRcxxrBalance: input.cxxrBalance * SPLIT_RATIO,
      splitRatio: SPLIT_RATIO,
    };
  },

  /**
   * Validate split can be executed
   */
  validateSplit(input: SplitExecutionInput): SplitValidationResult {
    const errors: string[] = [];

    if (!input.userId) {
      errors.push('User ID is required');
    }

    if (input.cxxrBalance <= 0) {
      errors.push('User has no CXXR balance to split');
    }

    return { valid: errors.length === 0, errors };
  },

  /**
   * Check if current price indicates peg deviation
   */
  checkPegDeviation(currentPrice: number, tolerance: number = 0.01): {
    isDeviated: boolean;
    deviationPercent: number;
    direction: 'above' | 'below' | 'stable';
  } {
    const pegPrice = 1.0;
    const deviation = currentPrice - pegPrice;
    const deviationPercent = Math.abs(deviation) * 100;

    return {
      isDeviated: Math.abs(deviation) > tolerance,
      deviationPercent: Math.round(deviationPercent * 100) / 100,
      direction: deviation > tolerance ? 'above' : deviation < -tolerance ? 'below' : 'stable',
    };
  },
};
`,

  'contracts/token/TransferContract.ts': `// contracts/token/TransferContract.ts
// ALL RIGHTS RESERVED

/**
 * Transfer Contract
 *
 * Business rules for CXXR token transfers (P2P).
 *
 * Contract Reference: Section 1.1.2
 * - "Collection of transaction fees (0.5% per transaction)"
 *
 * @module contracts/token/TransferContract
 */

import { FeeContract } from '../fees/FeeContract.js';

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════

/** Minimum transfer amount in CXXR */
export const MIN_TRANSFER_AMOUNT = 1;

/** Maximum transfer amount per transaction in CXXR */
export const MAX_TRANSFER_AMOUNT = 50000;

/** Maximum daily transfer amount per user in CXXR */
export const MAX_DAILY_TRANSFER_AMOUNT = 100000;

/** Required KYC level for transfers */
export const TRANSFER_REQUIRED_KYC_LEVEL = 'verified';

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export type TransferKycStatus = 'none' | 'pending' | 'verified' | 'rejected';

export interface TransferInput {
  senderId: string;
  recipientId: string;
  amount: number;
  senderKycStatus: TransferKycStatus;
  recipientKycStatus: TransferKycStatus;
  senderBalance: number;
  dailyTransferredAmount?: number;
}

export interface TransferOutput {
  amount: number;
  feeAmount: number;
  netAmount: number;
  feeDistribution: {
    cxxr: number;
    ccc: number;
    reserve: number;
  };
}

export interface TransferValidationResult {
  valid: boolean;
  errors: string[];
}

// ═══════════════════════════════════════════════════════
// CONTRACT
// ═══════════════════════════════════════════════════════

export const TransferContract = {
  // Constants exposed for transparency
  MIN_TRANSFER_AMOUNT,
  MAX_TRANSFER_AMOUNT,
  MAX_DAILY_TRANSFER_AMOUNT,
  TRANSFER_REQUIRED_KYC_LEVEL,

  /**
   * Validate transfer operation
   */
  validate(input: TransferInput): TransferValidationResult {
    const errors: string[] = [];

    // User validation
    if (!input.senderId) {
      errors.push('Sender ID is required');
    }

    if (!input.recipientId) {
      errors.push('Recipient ID is required');
    }

    if (input.senderId === input.recipientId) {
      errors.push('Cannot transfer to self');
    }

    // KYC validation
    if (input.senderKycStatus !== TRANSFER_REQUIRED_KYC_LEVEL) {
      errors.push(\`Sender KYC verification required (current: \${input.senderKycStatus})\`);
    }

    if (input.recipientKycStatus !== TRANSFER_REQUIRED_KYC_LEVEL) {
      errors.push(\`Recipient KYC verification required (current: \${input.recipientKycStatus})\`);
    }

    // Amount validation
    if (input.amount < MIN_TRANSFER_AMOUNT) {
      errors.push(\`Minimum transfer amount is \${MIN_TRANSFER_AMOUNT} CXXR\`);
    }

    if (input.amount > MAX_TRANSFER_AMOUNT) {
      errors.push(\`Maximum transfer amount is \${MAX_TRANSFER_AMOUNT} CXXR\`);
    }

    // Balance validation (sender must cover amount + fee)
    const feeResult = FeeContract.calculateFee({ type: 'transfer', amount: input.amount });
    const totalRequired = input.amount; // Fee is deducted from amount, not added
    if (input.senderBalance < totalRequired) {
      errors.push(
        \`Insufficient balance. Required: \${totalRequired} CXXR, Available: \${input.senderBalance} CXXR\`
      );
    }

    // Daily limit validation
    const dailyTotal = (input.dailyTransferredAmount || 0) + input.amount;
    if (dailyTotal > MAX_DAILY_TRANSFER_AMOUNT) {
      errors.push(
        \`Daily transfer limit exceeded. Limit: \${MAX_DAILY_TRANSFER_AMOUNT} CXXR, \` +
        \`Already transferred: \${input.dailyTransferredAmount || 0} CXXR\`
      );
    }

    return { valid: errors.length === 0, errors };
  },

  /**
   * Calculate transfer result
   *
   * Fee is deducted from the transfer amount.
   * Recipient receives: amount - 0.5% fee
   */
  calculateTransfer(input: TransferInput): TransferOutput {
    const validation = this.validate(input);
    if (!validation.valid) {
      throw new Error(\`Transfer validation failed: \${validation.errors.join(', ')}\`);
    }

    // Use FeeContract for fee calculation
    const feeResult = FeeContract.calculateFee({
      type: 'transfer',
      amount: input.amount,
    });

    // Use FeeContract for distribution
    const distribution = FeeContract.distributeFee(feeResult.feeAmount);

    return {
      amount: input.amount,
      feeAmount: feeResult.feeAmount,
      netAmount: feeResult.netAmount,
      feeDistribution: {
        cxxr: distribution.cxxr,
        ccc: distribution.ccc,
        reserve: distribution.reserve,
      },
    };
  },

  /**
   * Check if transfer is allowed
   */
  canTransfer(input: TransferInput): { allowed: boolean; reason?: string } {
    const validation = this.validate(input);
    if (!validation.valid) {
      return { allowed: false, reason: validation.errors[0] };
    }
    return { allowed: true };
  },

  /**
   * Get remaining daily limit for user
   */
  getRemainingDailyLimit(dailyTransferredAmount: number): number {
    return Math.max(0, MAX_DAILY_TRANSFER_AMOUNT - dailyTransferredAmount);
  },
};
`,

  'contracts/token/VestingContract.ts': `// contracts/token/VestingContract.ts
// ALL RIGHTS RESERVED

/**
 * Vesting Contract
 *
 * Business rules for time-based RCXXR vesting.
 *
 * Contract Reference: Section 1.1.2 (revised interpretation)
 * Original: "Token Split when market price exceeds $3.05"
 * Revised: Time-based vesting (quarterly) since 1:1 peg enforcement prevents price deviation
 *
 * Design:
 * - RCXXR is minted quarterly at 1:1 rate for continuous CXXR holders
 * - "Continuous" means balance never dropped to zero during the quarter
 * - Pro-rata vesting for partial quarters based on holding days
 *
 * @module contracts/token/VestingContract
 */

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════

/** Vesting period in days (one quarter) */
export const VESTING_PERIOD_DAYS = 90;

/** RCXXR minted per CXXR held (1:1) */
export const VESTING_RATE = 1.0;

/** Minimum CXXR balance required for vesting eligibility */
export const MIN_BALANCE_FOR_VESTING = 1.0;

/** Minimum days required for any vesting (prevent gaming) */
export const MIN_HOLDING_DAYS = 7;

/** Days in a quarter for pro-rata calculation */
export const QUARTER_DAYS = 90;

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export interface VestingEligibilityInput {
  /** User's minimum CXXR balance during the quarter */
  minBalance: number;
  
  /** Number of days with positive balance */
  holdingDays: number;
  
  /** Whether balance was continuous (never zero) */
  isContinuous: boolean;
  
  /** End-of-quarter CXXR balance */
  endBalance: number;
}

export interface VestingEligibilityOutput {
  /** Whether user is eligible for vesting */
  eligible: boolean;
  
  /** Reason if not eligible */
  reason?: string;
  
  /** Effective holding ratio (0-1) */
  holdingRatio: number;
}

export interface VestingCalculationInput {
  /** CXXR balance to use for calculation */
  cxxrBalance: number;
  
  /** Days held during the quarter */
  holdingDays: number;
  
  /** Total days in the quarter */
  quarterDays?: number;
  
  /** Custom vesting rate (default 1.0) */
  vestingRate?: number;
}

export interface VestingCalculationOutput {
  /** RCXXR amount to mint */
  rcxxrAmount: number;
  
  /** CXXR balance used */
  cxxrBalance: number;
  
  /** Effective rate applied */
  effectiveRate: number;
  
  /** Holding ratio used */
  holdingRatio: number;
}

export interface QuarterInfo {
  /** Quarter string (e.g., '2026-Q1') */
  quarter: string;
  
  /** Quarter start date */
  startDate: Date;
  
  /** Quarter end date */
  endDate: Date;
  
  /** Total days in quarter */
  totalDays: number;
}

export interface VestingValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

// ═══════════════════════════════════════════════════════
// CONTRACT
// ═══════════════════════════════════════════════════════

export const VestingContract = {
  // Constants exposed for transparency
  VESTING_PERIOD_DAYS,
  VESTING_RATE,
  MIN_BALANCE_FOR_VESTING,
  MIN_HOLDING_DAYS,
  QUARTER_DAYS,

  /**
   * Check if a user is eligible for vesting
   */
  checkEligibility(input: VestingEligibilityInput): VestingEligibilityOutput {
    // Must have maintained minimum balance
    if (input.minBalance < MIN_BALANCE_FOR_VESTING) {
      return {
        eligible: false,
        reason: \`Minimum balance of \${MIN_BALANCE_FOR_VESTING} CXXR required (had \${input.minBalance})\`,
        holdingRatio: 0,
      };
    }

    // Must have held for minimum days
    if (input.holdingDays < MIN_HOLDING_DAYS) {
      return {
        eligible: false,
        reason: \`Minimum \${MIN_HOLDING_DAYS} days of holding required (had \${input.holdingDays})\`,
        holdingRatio: 0,
      };
    }

    // Must have continuous holding (balance never zero)
    if (!input.isContinuous) {
      return {
        eligible: false,
        reason: 'Balance dropped to zero during quarter - continuous holding required',
        holdingRatio: 0,
      };
    }

    // Must have positive end balance
    if (input.endBalance <= 0) {
      return {
        eligible: false,
        reason: 'End-of-quarter balance must be positive',
        holdingRatio: 0,
      };
    }

    // Calculate holding ratio
    const holdingRatio = Math.min(input.holdingDays / QUARTER_DAYS, 1);

    return {
      eligible: true,
      holdingRatio,
    };
  },

  /**
   * Calculate RCXXR amount to mint based on holding
   *
   * Formula: RCXXR = CXXR_balance * vesting_rate * holding_ratio
   *
   * For full quarter holders: RCXXR = CXXR (1:1)
   * For partial quarter: Pro-rated based on holding days
   */
  calculateVestingAmount(input: VestingCalculationInput): VestingCalculationOutput {
    const quarterDays = input.quarterDays || QUARTER_DAYS;
    const vestingRate = input.vestingRate || VESTING_RATE;

    // Calculate holding ratio (capped at 1.0)
    const holdingRatio = Math.min(input.holdingDays / quarterDays, 1);

    // Calculate RCXXR amount
    const rcxxrAmount = input.cxxrBalance * vestingRate * holdingRatio;

    // Round to 8 decimal places
    const roundedAmount = Math.round(rcxxrAmount * 1e8) / 1e8;

    return {
      rcxxrAmount: roundedAmount,
      cxxrBalance: input.cxxrBalance,
      effectiveRate: vestingRate * holdingRatio,
      holdingRatio,
    };
  },

  /**
   * Validate vesting inputs
   */
  validate(input: VestingCalculationInput): VestingValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!input.cxxrBalance || input.cxxrBalance <= 0) {
      errors.push('CXXR balance must be positive');
    }

    if (input.cxxrBalance < MIN_BALANCE_FOR_VESTING) {
      errors.push(\`CXXR balance below minimum \${MIN_BALANCE_FOR_VESTING}\`);
    }

    if (input.holdingDays < 0) {
      errors.push('Holding days cannot be negative');
    }

    if (input.holdingDays < MIN_HOLDING_DAYS) {
      errors.push(\`Holding days below minimum \${MIN_HOLDING_DAYS}\`);
    }

    if (input.holdingDays > QUARTER_DAYS) {
      warnings.push(\`Holding days (\${input.holdingDays}) exceeds quarter (\${QUARTER_DAYS}), will be capped\`);
    }

    if (input.vestingRate && input.vestingRate !== VESTING_RATE) {
      warnings.push(\`Custom vesting rate \${input.vestingRate} differs from standard \${VESTING_RATE}\`);
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  },

  /**
   * Get quarter info for a given date
   */
  getQuarterInfo(date: Date = new Date()): QuarterInfo {
    const year = date.getFullYear();
    const month = date.getMonth();
    const quarterNum = Math.floor(month / 3) + 1;

    // Quarter start: first day of quarter
    const startMonth = (quarterNum - 1) * 3;
    const startDate = new Date(Date.UTC(year, startMonth, 1, 0, 0, 0, 0));

    // Quarter end: last day of quarter
    const endMonth = startMonth + 3;
    const endDate = new Date(Date.UTC(year, endMonth, 0, 23, 59, 59, 999));

    // Calculate total days
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    return {
      quarter: \`\${year}-Q\${quarterNum}\`,
      startDate,
      endDate,
      totalDays,
    };
  },

  /**
   * Get previous quarter info
   */
  getPreviousQuarterInfo(date: Date = new Date()): QuarterInfo {
    const currentQuarter = this.getQuarterInfo(date);
    const parts = currentQuarter.quarter.split('-Q');
    let year = parseInt(parts[0], 10);
    let quarterNum = parseInt(parts[1], 10);

    // Go back one quarter
    quarterNum -= 1;
    if (quarterNum === 0) {
      quarterNum = 4;
      year -= 1;
    }

    const startMonth = (quarterNum - 1) * 3;
    const startDate = new Date(Date.UTC(year, startMonth, 1, 0, 0, 0, 0));
    const endMonth = startMonth + 3;
    const endDate = new Date(Date.UTC(year, endMonth, 0, 23, 59, 59, 999));
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    return {
      quarter: \`\${year}-Q\${quarterNum}\`,
      startDate,
      endDate,
      totalDays,
    };
  },

  /**
   * Check if a date is within a quarter
   */
  isWithinQuarter(date: Date, quarter: string): boolean {
    const parts = quarter.split('-Q');
    const year = parseInt(parts[0], 10);
    const quarterNum = parseInt(parts[1], 10);

    const startMonth = (quarterNum - 1) * 3;
    const startDate = new Date(Date.UTC(year, startMonth, 1, 0, 0, 0, 0));
    const endMonth = startMonth + 3;
    const endDate = new Date(Date.UTC(year, endMonth, 0, 23, 59, 59, 999));

    return date >= startDate && date <= endDate;
  },

  /**
   * Calculate days between two dates
   */
  calculateDaysBetween(start: Date, end: Date): number {
    const diffMs = end.getTime() - start.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  },

  /**
   * Get vesting summary for display
   */
  getVestingSummary(input: {
    cxxrBalance: number;
    holdingDays: number;
    quarterDays: number;
  }): {
    eligibleForVesting: boolean;
    estimatedRcxxr: number;
    holdingProgress: number;
    daysRemaining: number;
  } {
    const holdingProgress = Math.min(input.holdingDays / input.quarterDays, 1);
    const daysRemaining = Math.max(input.quarterDays - input.holdingDays, 0);

    const eligibleForVesting =
      input.cxxrBalance >= MIN_BALANCE_FOR_VESTING && input.holdingDays >= MIN_HOLDING_DAYS;

    const estimatedRcxxr = eligibleForVesting
      ? input.cxxrBalance * VESTING_RATE * holdingProgress
      : 0;

    return {
      eligibleForVesting,
      estimatedRcxxr: Math.round(estimatedRcxxr * 1e8) / 1e8,
      holdingProgress,
      daysRemaining,
    };
  },
};

export default VestingContract;
`,

};

/** Look up source by file path */
export function getContractSource(sourceFile: string): string | null {
  return CONTRACT_SOURCES[sourceFile] ?? null;
}
